import { AudioSource, ContactType, Goal, PromptType, TranscriptSource } from "./consts"
import { jsonSafeValueToDate, jsonSafeValueToMaybeDate } from "./typing"
import { SESSION_METRICS_COLUMNS } from "./table"
import { PROSPECT_INFO_COLUMNS } from "./table"


export type Person = {
  person_id: string,
  team_id: string,
  person_name: string,
}

export function jsonSafeToPerson (value: Record<string, any>): Person {
  return {
    person_id: value.person_id,
    team_id: value.team_id,
    person_name: value.person_name
  }
}

export type Contact = {
  person_id: string,
  team_id: string,
  contact_type: ContactType,
  contact_value: string,
}

export function jsonSafeToContact (value: Record<string, any>): Contact {
  return {
    person_id: value.person_id,
    team_id: value.team_id,
    contact_type: value.contact_type,
    contact_value: value.contact_value,
  }
}

export type Party = {
  session_id: string,
  party_code: number,
  person_id: string,
}

export function jsonSafeToParty (value: Record<string, any>): Party {
  return {
    session_id: value.session_id,
    party_code: value.party_code,
    person_id: value.person_id,
  }
}

export type Note = {
  note_id: string,
  user_id: string,
  note: string,
  received_at: Date,
  session_id: string | null,
  note_source: string,
}

export function jsonSafeToNote (value: Record<string, any>): Note {
  return {
    note_id: value.note_id,
    user_id: value.user_id,
    note: value.note,
    received_at: jsonSafeValueToDate(value.received_at),
    session_id: value.session_id,
    note_source: value.note_source
  }
}

export type Session = {
  session_id: string,
  user_id: string,
  conversation_id: string
  requested_time: Date,
  realtime_hostname: string,
  scheduled_start: Date
  scheduled_end: Date
  team_id: string | null
  conversation_end: Date | null
  rep_party_code: number | null
  prospect_party_code: number | null
  prospect_person_id: string | null
  prospect_phone_value: string | null    
  prospect_recorded: boolean | null
  platform: Platform
  extension_version: string | null
  bot_id: string | null
}


export function jsonSafeToSession (value: Record<string, any>): Session {
  return {
    session_id: value.session_id,
    user_id: value.user_id,
    team_id: value.team_id,
    conversation_id: value.conversation_id,
    requested_time: jsonSafeValueToDate(value.requested_time),
    realtime_hostname: value.realtime_hostname,
    scheduled_start: jsonSafeValueToDate(value.scheduled_start),
    scheduled_end: jsonSafeValueToDate(value.scheduled_end),
    prospect_person_id: value.prospect_person_id,
    prospect_phone_value: value.prospect_phone_value,
    platform: value.platform,
    extension_version: value.extension_version,
    bot_id: value.bot_id,
    conversation_end: value.conversation_end ? jsonSafeValueToDate(value.conversation_end) : null,
    rep_party_code: value.rep_party_code,
    prospect_party_code: value.prospect_party_code,
    prospect_recorded: value.prospect_recorded,
  }
}

export enum ConversationType {
  CALL = 'CALL',
  MESSAGING = 'MESSAGING',
  CHAT = 'CHAT',
}

export enum SessionType {
  SALES ='SALES',
  INTAKE='INTAKE',
}

export enum AddressType {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  FACEBOOK = 'FACEBOOK',
  WEB = 'WEB',
}

export enum ResourceType {
  TEAM = 'TEAM',
  USER = 'USER',
  EXTERNAL_ACCOUNT = 'EXTERNAL_ACCOUNT',
}

export interface SessionV2 {
  session_id: string
  team_id: string
  base_path: string
  started_at: Date

  conversation_type: ConversationType
  call_type: SessionType | null

  customer_address: string | null
  agent_address: string | null
  address_type: AddressType | null

  resource_type: ResourceType | null
  resource_id: string

  is_unread: boolean | null
  is_flagged: boolean | null
}

export function jsonSafeToSessionV2 (value: Record<string, any>): SessionV2 {
  return {
    session_id: value.session_id,
    team_id: value.team_id,
    base_path: value.base_path,
    started_at: jsonSafeValueToDate(value.started_at),
    conversation_type: value.conversation_type,
    call_type: value.call_type ?? null,
    customer_address: value.customer_address ?? null,
    agent_address: value.agent_address ?? null,
    address_type: value.address_type ?? null,
    resource_type: value.resource_type ?? null,
    resource_id: value.resource_id ?? null,
    is_unread: value.is_unread ?? null,
    is_flagged: value.is_flagged ?? null,
  }
}

export type Transcript = {
  conversation_id: string
  party_code: number
  start: Date
  end: Date
  transcript_source: TranscriptSource
  bucket_key: string
  has_bucket: boolean
  audio_source: AudioSource | null
  run_id: string | null
  ext_transcript_stream_id: number | null
}

export function jsonSafeToTranscript (value: Record<string, any>): Transcript {
  return {
    conversation_id: value.conversation_id,
    party_code: value.party_code,
    start: jsonSafeValueToDate(value.start),
    end: jsonSafeValueToDate(value.end),
    transcript_source: value.transcript_source,
    bucket_key: value.bucket_key,
    has_bucket: value.has_bucket,
    audio_source: value.audio_source,
    run_id: value.run_id,
    ext_transcript_stream_id: value.ext_transcript_stream_id,
  }
}

export type NoteDisposition = {
  note_id: string,
  label: string,
  disposition: string,
}

export function jsonSafeToNoteDisposition (value: Record<string, any>): NoteDisposition {
  return {
    note_id: value.note_id,
    label: value.label,
    disposition: value.disposition,
  }
}

export type Display = {
  client_id: string,
  prompt_id: number,
  user_id: string,
  prompt_type: PromptType,
  prompt_text: string | null,
  start: Date,
  end: Date| null,
}

export function jsonSafeToDisplay (value: Record<string, any>): Display {
  return {
    client_id: value.client_id,
    prompt_id: value.prompt_id,
    user_id: value.user_id,
    prompt_type: value.prompt_type,
    prompt_text: value.prompt_text,
    start: jsonSafeValueToDate(value.start),
    end: jsonSafeValueToMaybeDate(value.end),
  }
}

export type SubmitNoteParams = {
  'note_source': string
  'notes': string
  'dispositions': {
    'label': string,
    'value': string,
  }[]
}


export const NO_PARTY_ROLE = 'NO_PARTY_ROLE'
export const NO_LOOKBACK_RULE = 'NO_LOOKBACK_RULE'

export enum AgentType {  // entity involved in a conversation, not to be confused with research agent (AGENT_TYPE)
  COACH = 'COACH',
  USER = 'USER',
  NUMBER = 'NUMBER',
  VOICE_BOT = 'VOICE_BOT',
  CHAT_BOT = 'CHAT_BOT',
  IVR = 'IVR',
  VOICEMAIL_DROP = 'VOICEMAIL_DROP',
}

export enum PartyRole {
  REP = 'REP',
  PROSPECT = 'PROSPECT'
}

export const PartyRoleToRegularCase: {[key in PartyRole]: string} = {
  [PartyRole.REP]: 'Rep',
  [PartyRole.PROSPECT]: 'Prospect'
}

export const PARTY_ROLE_TO_HUMAN_READABLE: {[key in PartyRole]: string} = {
  [PartyRole.REP]: 'Rep Time (%)',
  [PartyRole.PROSPECT]: 'Prospect Time (%)',
}


export enum Sound {
    TONE = 'TONE',  // includes tones over music
    MUSIC = 'MUSIC',
    SPEECH = 'SPEECH',
    SPEECH_OVER_MUSIC = 'SPEECH_OVER_MUSIC',
    BAD_DATA = 'BAD_DATA',  // corrupted and choppy data
    BACKGROUND = 'BACKGROUND'
}

export enum Counterpart {

    //////////////////////////////////////////////////
  // a live human
  //////////////////////////////////////////////////
  NOT_GATEKEEPER = 'NOT_GATEKEEPER',

  GATEKEEPER = 'GATEKEEPER',
  // people with a primary function of routing calls or controlling access, e.g. receptionists or assistants

  VOICEMAIL = 'VOICEMAIL',
  // recordings from the prospect phone system which primarily solicit a voice message to be recorded.
  // this includes:
  //   (a) follow-up menus (press one to send your message)
  //   (b) the end-of-voicemail beep
  //   (c) replay of a voicemail

  MENU = 'MENU',
  // recordings from the prospect phone system which primarily soliciting numeric user input.
  // includes contiguous advertisements from the same speaker that play before the menu starts
  // and ambiguous voicemail-like greeters that are not clearly voicemails
  // this includes notices that play before the call

  HOLD = 'HOLD',
  // recordings from the prospect phone system which primarily solicit the suer to wait
  // this includes
  //   (a) music
  //   (b) recorded interludes that play throughout a call (sometimes on top of music)
  //   (c) special unique recordings at the start of a hold, e.g. 'please wait while we connect your call'



    // tones
    RINGING = 'RINGING',

    // recordings
    CARRIER = 'CARRIER',
    //recordings from the phone infrastructure system or a third party (google voice, robokiller)
    // Ex: 'your call could not be completed as dialed'
    // Ex: 'this number is using robokiller to screen calls...' (ca1254654b1e11ed913095cb8eb5a87e)
    UNKNOWN = 'UNKNOWN',
}
export const MISC_COUNTERPART_FILTER_VALUE = 'MISC'

export const IS_COUNTERPART_MISC: {[key in Counterpart]: boolean} = {
  [Counterpart.RINGING]: true,
  [Counterpart.CARRIER]: true,
  [Counterpart.VOICEMAIL]: false,
  [Counterpart.MENU]: true,
  [Counterpart.HOLD]: true,
  [Counterpart.GATEKEEPER]: false,
  [Counterpart.NOT_GATEKEEPER]: false,
  [Counterpart.UNKNOWN]: true,
}

export const COUNTERPART_TO_HUMAN_READABLE: {[key in Counterpart]: string} = {
  [Counterpart.RINGING]: 'Ringing',
  [Counterpart.CARRIER]: 'Carrier',
  [Counterpart.VOICEMAIL]: 'Voicemail',
  [Counterpart.MENU]: 'Menu',
  [Counterpart.HOLD]: 'Hold',
  [Counterpart.GATEKEEPER]: 'Gatekeeper',
  [Counterpart.NOT_GATEKEEPER]: 'Prospect',
  [Counterpart.UNKNOWN]: 'Unknown'
}



export enum Remark {
  // permission
  C_ASKS = 'C_ASKS',
  R_DENY = 'R_DENY',
  R_BUSY = 'R_BUSY',
  R_APPROVE = 'R_APPROVE',
  
  // role
  C_NAME_CHECK = 'C_NAME_CHECK',
  R_NAME_DENY = 'R_NAME_DENY',
  R_NAME_CONFIRM = 'R_NAME_CONFIRM',
  R_NAME_STATE = 'R_NAME_STATE',
  C_TITLE_CHECK = 'C_TITLE_CHECK',
  R_TITLE_DENY = 'R_TITLE_DENY',
  R_TITLE_CONFIRM = 'R_TITLE_CONFIRM',
  R_TITLE_STATE = 'R_TITLE_STATE',
  C_RESP_CHECK = 'C_RESP_CHECK',
  R_RESP_NOT_RELEVANT = 'R_RESP_NOT_RELEVANT',
  R_RESP_NOT_DECISION_MAKER = 'R_RESP_NOT_DECISION_MAKER',
  R_RESP_DESCRIBE = 'R_RESP_DESCRIBE',
  C_TRANSFER_ASK = 'C_TRANSFER_ASK',
  R_TRANSFER_DENY = 'R_TRANSFER_DENY',
  R_TRANSFER_ATTEMPT = 'R_TRANSFER_ATTEMPT',
  C_REFERRAL_ASK = 'C_REFERRAL_ASK',
  R_REFERRAL_DENY = 'R_REFERRAL_DENY',
  R_REFERRAL_PROVIDE = 'R_REFERRAL_PROVIDE',
  R_IS_HOLD = 'R_IS_HOLD',
  R_IS_VOICEMAIL = 'R_IS_VOICEMAIL',
  R_IS_MENU = 'R_IS_MENU',
  R_IS_CARRIER = 'R_IS_CARRIER',
  
  // previous steps
  CALL_OUTBOUND = 'CALL_OUTBOUND',
  CALL_INBOUND = 'CALL_INBOUND',
  CALL_UNSPECIFIED = 'CALL_UNSPECIFIED',
  EMAIL_OUTBOUND = 'EMAIL_OUTBOUND',
  EMAIL_INBOUND = 'EMAIL_INBOUND',
  EMAIL_UNSPECIFIED = 'EMAIL_UNSPECIFIED',
  MEETING_SCHEDULED = 'MEETING_SCHEDULED',
  MEETING_COMPLETED = 'MEETING_COMPLETED',
  FORM = 'FORM',
  TRIAL = 'TRIAL',
  OTHER = 'OTHER',
  UNSPECIFIED = 'UNSPECIFIED',
  
  // next steps
  MEETING_TIME_SET = 'MEETING_TIME_SET',
  R_EMAIL_ADDRESS_CONFIRM = 'R_EMAIL_ADDRESS_CONFIRM',
  C_CALL_OFFER = 'C_CALL_OFFER',
  R_CALL_DENY = 'R_CALL_DENY',
  R_CALL_APPROVE = 'R_CALL_APPROVE',
  C_EMAIL_OFFER = 'C_EMAIL_OFFER',
  R_EMAIL_DENY = 'R_EMAIL_DENY',
  R_EMAIL_APPROVE = 'R_EMAIL_APPROVE',
  C_DELAYED_OFFER = 'C_DELAYED_OFFER',
  R_DELAYED_DENY = 'R_DELAYED_DENY',
  R_DELAYED_APPROVE = 'R_DELAYED_APPROVE',
  C_MEETING_OFFER = 'C_MEETING_OFFER',
  R_MEETING_REQUEST = 'R_MEETING_REQUEST',
  R_MEETING_DENY = 'R_MEETING_DENY',
  R_MEETING_CONFIRM = 'R_MEETING_CONFIRM',
  C_INBOUND_OFFER = 'C_INBOUND_OFFER',
  R_INBOUND_OFFER = 'R_INBOUND_OFFER',
  C_OTHER_OFFER = 'C_OTHER_OFFER',
  R_OTHER_APPROVE = 'R_OTHER_APPROVE',
  R_OTHER_DENY = 'R_OTHER_DENY',
  C_NONE_COMPANY = 'C_NONE_COMPANY',
  C_NONE_RECIPIENT = 'C_NONE_RECIPIENT',
  
  // product
  C_PRODUCT_DESCRIBE = 'C_PRODUCT_DESCRIBE',
  C_PRODUCT_ASK = 'C_PRODUCT_ASK',
  R_PRODUCT_ASK = 'R_PRODUCT_ASK',
  R_PRODUCT_NO_NEED = 'R_PRODUCT_NO_NEED',
  R_PRODUCT_NO_BUDGET = 'R_PRODUCT_NO_BUDGET',
  R_PRODUCT_DELAY = 'R_PRODUCT_DELAY',
  R_PRODUCT_INTERESTED = 'R_PRODUCT_INTERESTED',
  C_BUSINESS_ASK = 'C_BUSINESS_ASK',
  R_BUSINESS_DESCRIBE = 'R_BUSINESS_DESCRIBE',
  R_BUSINESS_SATISFIED = 'R_BUSINESS_SATISFIED',
  R_BUSINESS_NOT_SATISFIED = 'R_BUSINESS_NOT_SATISFIED',
  C_COMPETITORS_ASK = 'C_COMPETITORS_ASK',
  R_COMPETITORS_NOT_USING = 'R_COMPETITORS_NOT_USING',
  R_COMPETITORS_EVALUATING = 'R_COMPETITORS_EVALUATING',
  R_COMPETITORS_USING = 'R_COMPETITORS_USING'
}

export const REMARK_TO_SHOW_TRANSCRIPT: {[k in Remark]: boolean} = {
  [Remark.C_ASKS]: false,
  [Remark.R_DENY]: false,
  [Remark.R_BUSY]: true,
  [Remark.R_APPROVE]: true,
  [Remark.C_NAME_CHECK]: false,
  [Remark.C_TITLE_CHECK]: false,
  [Remark.R_NAME_DENY]: true,
  [Remark.R_NAME_CONFIRM]:false,
  [Remark.R_NAME_STATE]: false,
  [Remark.R_TITLE_DENY]: true,
  [Remark.R_TITLE_CONFIRM]: false,
  [Remark.R_TITLE_STATE]: true,
  [Remark.C_RESP_CHECK]: false,
  [Remark.R_RESP_NOT_RELEVANT]: true,
  [Remark.R_RESP_NOT_DECISION_MAKER]: true,
  [Remark.R_RESP_DESCRIBE]: true,
  [Remark.C_TRANSFER_ASK]: false,
  [Remark.R_TRANSFER_DENY]: false,
  [Remark.R_TRANSFER_ATTEMPT]: false,
  [Remark.C_REFERRAL_ASK]: true,
  [Remark.R_REFERRAL_DENY]: true,
  [Remark.R_REFERRAL_PROVIDE]: true,
  [Remark.R_IS_HOLD]: false,
  [Remark.R_IS_VOICEMAIL]: false,
  [Remark.R_IS_MENU]: false,
  [Remark.R_IS_CARRIER]: false,
  [Remark.CALL_OUTBOUND]: true,
  [Remark.CALL_INBOUND]: true,
  [Remark.CALL_UNSPECIFIED]: true,
  [Remark.EMAIL_OUTBOUND]: true,
  [Remark.EMAIL_INBOUND]: true,
  [Remark.EMAIL_UNSPECIFIED]: true,
  [Remark.MEETING_SCHEDULED]: true,
  [Remark.MEETING_COMPLETED]: true,
  [Remark.FORM]: true,
  [Remark.TRIAL]: true,
  [Remark.OTHER]: true,
  [Remark.UNSPECIFIED]: false,
  [Remark.MEETING_TIME_SET]: false,
  [Remark.R_EMAIL_ADDRESS_CONFIRM]: false,
  [Remark.C_CALL_OFFER]: false,
  [Remark.R_CALL_DENY]: false,
  [Remark.R_CALL_APPROVE]: false,
  [Remark.C_EMAIL_OFFER]: false,
  [Remark.R_EMAIL_DENY]: true,
  [Remark.R_EMAIL_APPROVE]: true,
  [Remark.C_DELAYED_OFFER]: false,
  [Remark.R_DELAYED_DENY]: true,
  [Remark.R_DELAYED_APPROVE]: true,
  [Remark.C_MEETING_OFFER]: true,
  [Remark.R_MEETING_REQUEST]: true,
  [Remark.R_MEETING_DENY]: true,
  [Remark.R_MEETING_CONFIRM]: true,
  [Remark.C_INBOUND_OFFER]: false,
  [Remark.R_INBOUND_OFFER]: false,
  [Remark.C_OTHER_OFFER]: true,
  [Remark.R_OTHER_APPROVE]: true,
  [Remark.R_OTHER_DENY]: true,
  [Remark.C_NONE_COMPANY]: false,
  [Remark.C_NONE_RECIPIENT]: false,
  [Remark.C_PRODUCT_DESCRIBE]: true,
  [Remark.C_PRODUCT_ASK]: true,
  [Remark.R_PRODUCT_ASK]: true,
  [Remark.R_PRODUCT_NO_NEED]: true,
  [Remark.R_PRODUCT_NO_BUDGET]: true,
  [Remark.R_PRODUCT_DELAY]: true,
  [Remark.R_PRODUCT_INTERESTED]: true,
  [Remark.C_BUSINESS_ASK]: true,
  [Remark.R_BUSINESS_DESCRIBE]: true,
  [Remark.R_BUSINESS_SATISFIED]: true,
  [Remark.R_BUSINESS_NOT_SATISFIED]: true,
  [Remark.C_COMPETITORS_ASK]: false,
  [Remark.R_COMPETITORS_NOT_USING]: true,
  [Remark.R_COMPETITORS_EVALUATING]: true,
  [Remark.R_COMPETITORS_USING]: true
}

export const REMARK_TO_HUMAN_READABLE: {[k in Remark]: string} = {
  [Remark.C_ASKS]: "Asks permission for call",
  [Remark.R_DENY]: "Rejects taking call",
  [Remark.R_BUSY]: "Is currently busy to take call",
  [Remark.R_APPROVE]: "Agrees to listen to rep",
  [Remark.C_NAME_CHECK]: "Confirms prospect name",
  [Remark.C_TITLE_CHECK]: "Asks about title",
  [Remark.R_NAME_DENY]: "Responds they are not the intended prospect",
  [Remark.R_NAME_CONFIRM]: "Verifies they are the intended prospect",
  [Remark.R_NAME_STATE]: "States their name",
  [Remark.R_TITLE_DENY]: "States that they are not in that role",
  [Remark.R_TITLE_CONFIRM]: "Confirms they are in the relevant role",
  [Remark.R_TITLE_STATE]: "States their role",
  [Remark.C_RESP_CHECK]: "Asks about responsibilities",
  [Remark.R_RESP_NOT_RELEVANT]: "Indicates the call is not relevant to their role",
  [Remark.R_RESP_NOT_DECISION_MAKER]: "Indicates they are not a decision maker",
  [Remark.R_RESP_DESCRIBE]: "Describes their role",
  [Remark.C_TRANSFER_ASK]: "Asks to be transferred to prospect",
  [Remark.R_TRANSFER_DENY]: "Rejects reps transfer request",
  [Remark.R_TRANSFER_ATTEMPT]: "Attempt to transfer rep",
  [Remark.C_REFERRAL_ASK]: "Asks for a referral",
  [Remark.R_REFERRAL_DENY]: "Does not provide a referral",
  [Remark.R_REFERRAL_PROVIDE]: "Provides referral",
  [Remark.R_IS_HOLD]: "Call is on hold",
  [Remark.R_IS_VOICEMAIL]: "Voicemail",
  [Remark.R_IS_MENU]: "Menu",
  [Remark.R_IS_CARRIER]: "Carrier Message",
  [Remark.CALL_OUTBOUND]: "Received a previous call from rep",
  [Remark.CALL_INBOUND]: "Previously made a call to rep's company",
  [Remark.CALL_UNSPECIFIED]: "Discussion on previous call",
  [Remark.EMAIL_OUTBOUND]: "Received emails from rep",
  [Remark.EMAIL_INBOUND]: "Emailed rep's company",
  [Remark.EMAIL_UNSPECIFIED]: "Discussion on previous emails between rep and prospect",
  [Remark.MEETING_SCHEDULED]: "Already scheduled a meeting with rep's company",
  [Remark.MEETING_COMPLETED]: "Has already met with rep's company",
  [Remark.FORM]: "Submitted a form on the website",
  [Remark.TRIAL]: "Is participating in a trial of rep's product",
  [Remark.OTHER]: "Previous interaction being discussed",
  [Remark.UNSPECIFIED]: "Unspecified previous interaction",
  [Remark.MEETING_TIME_SET]: "Meeting time scheduling discussion",
  [Remark.R_EMAIL_ADDRESS_CONFIRM]: "Confirms their email address",
  [Remark.C_CALL_OFFER]: "Offers to / states they will give a call back",
  [Remark.R_CALL_DENY]: "Says they do not want a call back",
  [Remark.R_CALL_APPROVE]: "Agrees to the call back",
  [Remark.C_EMAIL_OFFER]: "Confirms sending email",
  [Remark.R_EMAIL_DENY]: "Says they do not want an email",
  [Remark.R_EMAIL_APPROVE]: "Agrees to receiving an email",
  [Remark.C_DELAYED_OFFER]: "Offers to reach back out in a while",
  [Remark.R_DELAYED_DENY]: "Says they do not want a call back",
  [Remark.R_DELAYED_APPROVE]: "Agrees to being reached back out in a while",
  [Remark.C_MEETING_OFFER]: "Offers a scheduled meeting",
  [Remark.R_MEETING_REQUEST]: "Requests a scheduled meeting",
  [Remark.R_MEETING_DENY]: "Rejects the meeting",
  [Remark.R_MEETING_CONFIRM]: "Agrees to the meeting",
  [Remark.C_INBOUND_OFFER]: "Tells prospect to reach out if they ever have any questions",
  [Remark.R_INBOUND_OFFER]: "Offers to reach back out if they have questions",
  [Remark.C_OTHER_OFFER]: "Invites prospect to a touchpoint",
  [Remark.R_OTHER_APPROVE]: "Agrees to touchpoint",
  [Remark.R_OTHER_DENY]: "Rejects touchpoint",
  [Remark.C_NONE_COMPANY]: "States they will not contact prospect's company any further",
  [Remark.C_NONE_RECIPIENT]: "States they will not contact prospect any further",
  [Remark.C_PRODUCT_DESCRIBE]: "Describes product",
  [Remark.C_PRODUCT_ASK]: "Asks about value of product to prospect",
  [Remark.R_PRODUCT_ASK]: "Asks about product",
  [Remark.R_PRODUCT_NO_NEED]: "Says they have no need for product",
  [Remark.R_PRODUCT_NO_BUDGET]: "Says they have no budget to consider new tools",
  [Remark.R_PRODUCT_DELAY]: "Says the timing does not work out to consider new tools",
  [Remark.R_PRODUCT_INTERESTED]: "Expresses interest in the product",
  [Remark.C_BUSINESS_ASK]: "Asks about business",
  [Remark.R_BUSINESS_DESCRIBE]: "Describes their business",
  [Remark.R_BUSINESS_SATISFIED]: "States their business needs are met",
  [Remark.R_BUSINESS_NOT_SATISFIED]: "States their business needs are not met",
  [Remark.C_COMPETITORS_ASK]: "Asks if prospect is using any competitors",
  [Remark.R_COMPETITORS_NOT_USING]: "Says they are not using any competitors",
  [Remark.R_COMPETITORS_EVALUATING]: "Says they are evaluating competitors",
  [Remark.R_COMPETITORS_USING]: "Says they are using a competitor"
}

export const OBJECTION_REMARKS: Remark[] = [
  Remark.R_RESP_NOT_RELEVANT,
  Remark.R_RESP_NOT_DECISION_MAKER,
  Remark.R_PRODUCT_NO_NEED,
  Remark.R_PRODUCT_NO_BUDGET,
  Remark.R_COMPETITORS_USING,
  Remark.R_DENY,
  Remark.R_NAME_DENY,
  Remark.R_TITLE_DENY
]

export const RemarksToShortForm: Map<Remark, string> = new Map([
  // objection remarks
  [Remark.R_RESP_NOT_RELEVANT, 'Not relevant'],
  [Remark.R_RESP_NOT_DECISION_MAKER, 'Not a decision maker'],
  [Remark.R_PRODUCT_NO_NEED, 'No need'],
  [Remark.R_PRODUCT_NO_BUDGET, 'No budget'],
  [Remark.R_COMPETITORS_USING, 'Using competitor'],
  [Remark.R_DENY, 'Call brush off'],
  [Remark.R_NAME_DENY, 'Wrong person'],
  [Remark.R_TITLE_DENY, 'Wrong role'],

  // behavior remarks
  [Remark.C_ASKS, "Asks permission for call"],
  [Remark.C_REFERRAL_ASK, "Asks for referral"],
  [Remark.C_MEETING_OFFER, "Asks for meeting"],
  [Remark.C_CALL_OFFER, "Offers to call back"],
  [Remark.C_BUSINESS_ASK, "Asks about competitors"],

  // outcome remarks
  [Remark.R_REFERRAL_PROVIDE, "Provides referral"],
  [Remark.R_CALL_APPROVE, "Agrees to call back"],
  [Remark.R_MEETING_CONFIRM, "Confirms meeting"],
  [Remark.R_PRODUCT_INTERESTED, "Expressed product interest"] 
])

export const REMARKS_OBJECTIONS: Map<Remark, [string, string]> = new Map([
  [Remark.R_RESP_NOT_RELEVANT, ['Not relevant', 'Objections']],
  [Remark.R_RESP_NOT_DECISION_MAKER, ['Not a decision maker', 'Objections']],
  [Remark.R_PRODUCT_NO_NEED, ['No need', 'Objections']],
  [Remark.R_PRODUCT_NO_BUDGET, ['No budget', 'Objections']],
  [Remark.R_PRODUCT_DELAY, ['Timing issue', 'Objections']],
  [Remark.R_COMPETITORS_USING, ['Using competitor', 'Objections']],
  [Remark.R_DENY, ['Call brush off', 'Objections']],
  [Remark.R_NAME_DENY, ['Wrong person', 'Objections']],
  [Remark.R_TITLE_DENY, ['Wrong role', 'Objections']]
])

export const BEHAVIOR_REMARKS = [
  Remark.C_ASKS,
  Remark.C_REFERRAL_ASK,
  Remark.C_MEETING_OFFER,
  Remark.C_CALL_OFFER,
  Remark.C_BUSINESS_ASK,
]

export const REMARKS_BEHAVIORS: Map<Remark, [string, string]> = new Map([
  [Remark.C_ASKS, ['Asks permission for call', 'Behavior']],
  [Remark.C_REFERRAL_ASK, ['Asks for referral', 'Behavior']],
  [Remark.C_MEETING_OFFER, ['Asks for meeting', 'Behavior']],
  [Remark.C_CALL_OFFER, ['Offers to call back', 'Behavior']],
  [Remark.C_BUSINESS_ASK, ['Asks about business', 'Behavior']],
  [Remark.C_COMPETITORS_ASK, ['Asks about competitors', 'Behavior']],
])

export const OUTCOME_REMARKS = [
  Remark.R_REFERRAL_PROVIDE,
  Remark.R_CALL_APPROVE,
  Remark.R_MEETING_CONFIRM,
  Remark.R_PRODUCT_INTERESTED,
]

export const REMARKS_OUTCOMES: Map<Remark, [string, string]> = new Map([
  [Remark.R_REFERRAL_PROVIDE, ['Provides referral', 'Outcomes']],
  [Remark.R_CALL_APPROVE, ['Agrees to call back',  'Outcomes']],
  [Remark.R_MEETING_CONFIRM, ['Confirms meeting or reconnect',  'Outcomes']],
  [Remark.MEETING_TIME_SET, ['Meeting scheduling',  'Outcomes']],
  [Remark.R_PRODUCT_INTERESTED, ['Expresses interest',  'Outcomes']],
])


export const REMARKS_ALLOWED: Remark[] = [
  Remark.R_DENY, // states they do not want call to continue explicitly
  Remark.R_NAME_DENY, // negative assertion of name
  Remark.R_TITLE_DENY, // negativge assertion of title
  Remark.R_TRANSFER_DENY, // transfer attempt rejected
  Remark.R_RESP_NOT_DECISION_MAKER, // not decision maker
  Remark.R_RESP_NOT_RELEVANT, // not relevant
  Remark.R_CALL_DENY, // denies a call back
  Remark.R_EMAIL_DENY, // denies email 
  Remark.R_DELAYED_DENY, // denies delayed call back
  Remark.R_MEETING_DENY, // denies meeting offer
  Remark.R_PRODUCT_NO_NEED, // no need for product
  Remark.R_PRODUCT_NO_BUDGET, // budget objection
  Remark.R_COMPETITORS_USING, // using competitor
  Remark.C_ASKS, // permision assking
  Remark.C_REFERRAL_ASK, // asked for referral
  Remark.C_BUSINESS_ASK, // asks about business
  Remark.C_RESP_CHECK, // asks about responsibilities
  Remark.C_MEETING_OFFER, // offers meeting
  Remark.C_CALL_OFFER, // offers to call back
  Remark.C_EMAIL_OFFER, // offers to send email
  Remark.R_EMAIL_APPROVE, // approves receiving email
  Remark.R_PRODUCT_INTERESTED, // expresses interest in product
  Remark.R_MEETING_CONFIRM, // confirms meeting
  Remark.R_CALL_APPROVE, // approves a call back
  Remark.R_EMAIL_APPROVE, // approves receiving email
  Remark.R_REFERRAL_PROVIDE, // provides referral
]

export const OBJECTION_REMARKS_ALLOWED: Remark[] = [
  Remark.R_DENY,
  Remark.R_NAME_DENY,
  Remark.R_TITLE_DENY,
  Remark.R_TRANSFER_DENY,
  Remark.R_RESP_NOT_DECISION_MAKER,
  Remark.R_RESP_NOT_RELEVANT,
  Remark.R_CALL_DENY,
  Remark.R_DELAYED_DENY,
  Remark.R_EMAIL_DENY,
  Remark.R_MEETING_DENY,
  Remark.R_PRODUCT_NO_NEED,
  Remark.R_PRODUCT_NO_BUDGET,
  Remark.R_COMPETITORS_USING,
]

export const REP_BEHAVIOR_REMARKS_ALLOWED: Remark[] = [
  Remark.C_ASKS,
  Remark.C_REFERRAL_ASK,
  Remark.C_BUSINESS_ASK,
  Remark.C_RESP_CHECK,
  Remark.C_MEETING_OFFER,
  Remark.C_CALL_OFFER,
  Remark.C_EMAIL_OFFER,
]

export enum REMARK_ELIGIBLE {
  REP_ONLY = 'REP_ONLY',
  PROSPECT_ONLY = 'PROSPECT_ONLY',
  BOTH = 'BOTH',
}

export const RemarkToEligible = Object.fromEntries(Array.from(Object.values(Remark)).map((value: Remark) => {
  return [value as Remark, value.startsWith('C_') ? REMARK_ELIGIBLE.REP_ONLY : value.startsWith('R_') ? REMARK_ELIGIBLE.PROSPECT_ONLY : REMARK_ELIGIBLE.BOTH]
}))


export const OBJECTIONS_TO_HUMAN_READABLE: Map<Remark, string> = new Map([
[Remark.R_DENY, 'Bad time'],
[Remark.R_NAME_DENY, 'Wrong Name'],
[Remark.R_TITLE_DENY, 'Wrong Role'],
[Remark.R_TRANSFER_DENY, 'No Transfer'],
[Remark.R_RESP_NOT_DECISION_MAKER, 'No Decision Maker'],
[Remark.R_CALL_DENY, 'Busy Now'],
[Remark.R_EMAIL_DENY, 'No Email'],
[Remark.R_DELAYED_DENY, 'Delay'],
[Remark.R_MEETING_DENY, 'Meeting Denied'],
[Remark.R_PRODUCT_NO_NEED, 'No Product Need'],
[Remark.R_PRODUCT_NO_BUDGET, 'No Budget'],
[Remark.R_COMPETITORS_USING, 'Using Competitors'],
[Remark.R_RESP_NOT_RELEVANT, 'Not Relevant'],
])


export const REMARK_FILTERS_TO_HUMAN_READABLE = new Map([...Array.from(REMARKS_OBJECTIONS), ...Array.from(REMARKS_BEHAVIORS), ...Array.from(REMARKS_OUTCOMES)].map((value: [Remark, [string, string]]) => [value[0], value[1][0]]))

export const ANALYTICS_REMARKS_TO_HUMAN_READABLE: Map<Remark, [string, string]> = new Map([...Array.from(REMARKS_BEHAVIORS.entries()), ...Array.from(REMARKS_OBJECTIONS.entries()), ...Array.from(REMARKS_OUTCOMES.entries())])

export enum AGENT_TYPE {  // research agent, not to be confused with entity involved in a conversation (AgentType)
  'LINKEDIN' = 'LINKEDIN',
  'COMPANY_WEBSITE' = 'COMPANY_WEBSITE'
}

export enum STAGE {
    DIALED = 'DIALED',
    CONTACT = 'CONTACT',  
    PITCHED = 'PITCHED',
    CONVERSATION = 'CONVERSATION',
    BOOKED = 'BOOKED'
}

export const StageToHumanReadable: {[k in STAGE]: string} = {
  [STAGE.DIALED]: "Dialed",
  [STAGE.CONTACT]: "Live Prospect",
  [STAGE.PITCHED]: "Pitched",
  [STAGE.CONVERSATION]: "Conversation",
  [STAGE.BOOKED]: "Meeting booked"
}

export type SubTeam = {
  subteam_id: string
  team_id: string
  subteam_name: string
}


export enum Platform {
  CLAY = 'CLAY',
  ZOOM = 'ZOOM',
  SALESLOFT = 'SALESLOFT',
  OUTREACH = 'OUTREACH',
  ORUM = 'ORUM',
  OUTREACH_RINGCENTRAL = 'OUTREACH_RINGCENTRAL',
  HUBSPOT = 'HUBSPOT',
  APOLLO = 'APOLLO',
  PHONEBURNER = 'PHONEBURNER',
  SALESFINITY = 'SALESFINITY',
  CONNECT_AND_SELL = 'CONNECT_AND_SELL',
  MONSTER_CONNECT = 'MONSTER_CONNECT',
  ALOWARE = 'ALOWARE',
  AIRCALL = 'AIRCALL',
  OUTPLAY = 'OUTPLAY',
  KONCERT = 'KONCERT',
  ONEPGR = 'ONEPGR',
  NOOKS = 'NOOKS',
  CONVOSO = 'CONVOSO',
  FRONTSPIN = 'FRONTSPIN',
  SELLX = 'SELLX',
  FRESHWORKS = 'FRESHWORKS',
  EIGHT_BY_EIGHT = 'EIGHT_BY_EIGHT',
  CALLRAIL = 'CALLRAIL',
  ZOHO = 'ZOHO',
  SALESFORCE = 'SALESFORCE',
  LAWRULER = 'LAWRULER',
  DIALPAD = 'DIALPAD',
  OPENPHONE = 'OPENPHONE',
  VICIDIAL = 'VICIDIAL',
  GOHIGHLEVEL = 'ASPIRED_BUSINESS_COACH',
  TWOXCONNECT = 'TWOXCONNECT',
  SKYPE = 'SKYPE',
  RINGCENTRAL = 'RINGCENTRAL',
  ELTO = 'ELTO',
  ZOOMINFO = 'ZOOMINFO',
  KOMMO = 'KOMMO',
  BITRIX = 'BITRIX',
  BLOOBIRDS = 'BLOOBIRDS',
  CLOUDTALK = 'CLOUDTALK',
  FIVENINE = 'FIVENINE',
  CALLTOOLS = 'CALLTOOLS',
  GONG_ENGAGE = 'GONG_ENGAGE',
  TRELLUS_DIALER = 'TRELLUS_DIALER',
  AMPLEMARKET = 'AMPLEMARKET',
  NEXTIVA = 'NEXTIVA',
  VANILLASOFT = 'VANILLASOFT',
  GOOGLE_SHEETS = 'GOOGLE_SHEETS',
  UNIFY = 'UNIFY',
  LEMLIST = 'LEMLIST',
  AGENTLOCATOR = 'AGENTLOCATOR',
  CLOSE = 'CLOSE'
}

export const PLATFORM_TO_HUMAN_READABLE: {
  [k in Platform]: string;
} = {
  [Platform.CLAY]: 'Clay',
  [Platform.ZOOM]: 'Zoom',
  [Platform.SALESLOFT]: 'Salesloft',
  [Platform.OUTREACH]: 'Outreach',
  [Platform.ORUM]: 'Orum',
  [Platform.OUTREACH_RINGCENTRAL]: 'Outreach RingCentral',
  [Platform.HUBSPOT]: 'HubSpot',
  [Platform.APOLLO]: 'Apollo',
  [Platform.PHONEBURNER]: 'PhoneBurner',
  [Platform.SALESFINITY]: 'Salesfinity',
  [Platform.CONNECT_AND_SELL]: 'ConnectAndSell',
  [Platform.MONSTER_CONNECT]: 'MonsterConnect',
  [Platform.ALOWARE]: 'Aloware',
  [Platform.AIRCALL]: 'Aircall',
  [Platform.OUTPLAY]: 'Outplay',
  [Platform.KONCERT]: 'Koncert',
  [Platform.ONEPGR]: 'OnePGR',
  [Platform.NOOKS]: 'Nooks',
  [Platform.CONVOSO]: 'Convoso',
  [Platform.FRONTSPIN]: 'FrontSpin',
  [Platform.SELLX]: 'SellX',
  [Platform.FRESHWORKS]: 'Freshworks',
  [Platform.EIGHT_BY_EIGHT]: '8x8',
  [Platform.CALLRAIL]: 'CallRail',
  [Platform.ZOHO]: 'Zoho',
  [Platform.SALESFORCE]: 'Salesforce',
  [Platform.LAWRULER]: 'LawRuler',
  [Platform.DIALPAD]: 'Dialpad',
  [Platform.OPENPHONE]: 'OpenPhone',
  [Platform.VICIDIAL]: 'VICIdial',
  [Platform.GOHIGHLEVEL]: 'GoHighLevel',
  [Platform.TWOXCONNECT]: '2XConnect',
  [Platform.SKYPE]: 'Skype',
  [Platform.RINGCENTRAL]: 'RingCentral',
  [Platform.ELTO]: 'Elto',
  [Platform.ZOOMINFO]: 'ZoomInfo',
  [Platform.KOMMO]: 'Kommo',
  [Platform.BITRIX]: 'Bitrix',
  [Platform.BLOOBIRDS]: 'Bloobirds',
  [Platform.CLOUDTALK]: 'CloudTalk',
  [Platform.FIVENINE]: 'Five9',
  [Platform.CALLTOOLS]: 'CallTools',
  [Platform.GONG_ENGAGE]: 'Gong Engage',
  [Platform.TRELLUS_DIALER]: 'Trellus',
  [Platform.AMPLEMARKET]: 'Amplemarket',
  [Platform.NEXTIVA]: 'Nextiva',
  [Platform.VANILLASOFT]: 'VanillaSoft',
  [Platform.GOOGLE_SHEETS]: 'Google Sheets',
  [Platform.UNIFY]: 'Unify',
  [Platform.LEMLIST]: 'Lemlist',
  [Platform.AGENTLOCATOR]: 'Agent Locator',
  [Platform.CLOSE]: 'Close'
}

export enum PROSPECT_PHONE_TYPE {
  MOBILE = 'MOBILE',
  VOIP = 'VOIP',
  WORK = 'WORK',
  HOME = 'HOME',
  OTHER = 'OTHER'
}

export const PHONE_TYPE_TO_HUMAN_READABLE: {[k in PROSPECT_PHONE_TYPE]: string} = {
  [PROSPECT_PHONE_TYPE.MOBILE]: 'Mobile',
  [PROSPECT_PHONE_TYPE.VOIP]: 'VoIP',
  [PROSPECT_PHONE_TYPE.WORK]: 'Work',
  [PROSPECT_PHONE_TYPE.HOME]: 'Home',
  [PROSPECT_PHONE_TYPE.OTHER]: 'Other'
};

export const UNKNOWN_PHONE_TYPE = 'Unknown';

export type UserGroup = {
  user_group_id: string
  user_group_name: string
  team_id: string
}

export type UserGroupMembership = {
  user_group_id: string
  member_id: string
  member_is_group: boolean
  team_id: string
}

export type EmailValueProp = {
  email_value_prop_id: string 
  pain_point: string
  prospect_benefits: string
  solution: string
  user_id: string | null
  team_id: string | null
  email_domain: string | null
}

export enum EmailToneType {
  SOLUTION_CENTERED = 'SOLUTION_CENTERED',
  PROBLEM_CENTERED = 'PROBLEM_CENTERED',
  FORMAL = 'FORMAL',
  CASUAL = 'CASUAL',
  FUNNY = 'FUNNY',
  SHORT = 'SHORT',
}

export const EMAIL_TONE_TYPE_TO_HUMAN_READABLE: {[k in EmailToneType]: string} = {
  [EmailToneType.FORMAL]: 'Formal',
  [EmailToneType.CASUAL]: 'Casual',
  [EmailToneType.FUNNY]: 'Funny',
  [EmailToneType.SHORT]: 'Shorten',
  [EmailToneType.PROBLEM_CENTERED]: 'Problem Centered',
  [EmailToneType.SOLUTION_CENTERED]: 'Solution Centered',
}

export type AIProspectEmail = {
  user_id: string
  prospect_person_id: string
  gen_email: string
  updated_at: Date
}

export function jsonSafeToAIProspectEmail(v: Record<string, any>): AIProspectEmail {
  return {
    user_id: v.user_id,
    prospect_person_id: v.prospect_person_id,
    gen_email: v.gen_email,
    updated_at: jsonSafeValueToDate(v.updated_at)
  }
}

export type LinkedInGeneratedMessage = {
  user_id: string
  platform_person_url: string
  gen_message: string
  updated_at: Date
}

export function jsonSafeToLinkedInGeneratedEmail(v: Record<string, any>): LinkedInGeneratedMessage {
  return {
    user_id: v.user_id,
    platform_person_url: v.platform_person_url,
    gen_message: v.gen_message,
    updated_at: jsonSafeValueToDate(v.updated_at)
  }
}

export type RegisteredTwilioNumber = {
  number: string,
  user_id: string | null
  team_id: string | null
  country: string
  locality: string | null,
  region: string | null,
  postal_code: string | null,
  latitude: number | null,
  longitude: number | null
  nomorobo_spamscore: number | null
  nomorobo_spamscore_as_of: Date | null
  twilio_messaging_sid: string | null
  free_caller_registry_status: 'ERROR' | 'REJECTED' | 'SUBMITTED' | null
  free_caller_registry_status_as_of: Date | null
  ipqualityscore_as_of: Date | null
  ipqualityscore_score: number | null
  ipqualityscore_risky: boolean | null
  ipqualityscore_abuse: boolean | null
  ipqualityscore_spammer: boolean | null
}

export function jsonSafeToRegisteredTwilioNumber(v: Record<string, any>): RegisteredTwilioNumber {
  return {
    number: v.number ?? v.rep_phone_value,
    user_id: v.user_id,
    team_id: v.team_id,
    country: v.country,
    locality: v.locality,
    region: v.region,
    postal_code: v.postal_code,
    latitude: v.latitude,
    longitude: v.longitude,
    nomorobo_spamscore: v.nomorobo_spamscore,
    nomorobo_spamscore_as_of: jsonSafeValueToMaybeDate(v.nomorobo_spamscore_as_of),
    twilio_messaging_sid: v.twilio_messaging_sid,
    free_caller_registry_status: v.free_caller_registry_status,
    free_caller_registry_status_as_of: jsonSafeValueToMaybeDate(v.free_caller_registry_status_as_of),
    ipqualityscore_as_of: jsonSafeValueToMaybeDate(v.ipqualityscore_as_of),
    ipqualityscore_score: v.ipqualityscore_score,
    ipqualityscore_risky: v.ipqualityscore_risky,
    ipqualityscore_abuse: v.ipqualityscore_abuse,
    ipqualityscore_spammer: v.ipqualityscore_spammer
  }
}

export function jsonSafeToRegisteredTwilioNumbers(v: Record<string, any>[]): RegisteredTwilioNumber[] {
  return v.map(jsonSafeToRegisteredTwilioNumber)
}

export type OptionalTwilioNumber = {
  number_type: 'Local' | 'Mobile' | 'TollFree',
  friendly_name: string,
  phone_number: string,
  locality: string,
  region: string,
  iso_country: string,
  postal_code: string,
  latitude: number,
  longitude: number
}

export type TwilioBaseRegistration = {
  team_id: string
  bundle_sid: string
  bundle_status: string
  status_as_of: Date
  status_reason: string
}

export type TwilioCustomerProfileRegistration = TwilioBaseRegistration & {
  purpose: 'CUSTOMER_PROFILE'
  registered_name: string
}

export type TwilioCnamRegistration = TwilioBaseRegistration & {
  purpose: 'CNAM'
  cnam_display_name: string
}

export type TwilioNumberRentalRegistration = TwilioBaseRegistration & {
  purpose: 'NUMBER_RENTAL'
  number_rental_type: string
}

export type TwilioOtherRegistration = TwilioBaseRegistration & {
  purpose: 'SHAKEN_STIR' | 'VOICE_INTEGRITY' | 'BRANDED_CALLS' | 'A2P_MESSAGING'
}

export type TwilioRegistration = TwilioCustomerProfileRegistration | TwilioCnamRegistration | TwilioNumberRentalRegistration | TwilioOtherRegistration

export function jsonSafeToTwilioRegistration(v: Record<string, any>): TwilioRegistration {
  const base: TwilioBaseRegistration = {
    team_id: v.team_id,
    bundle_sid: v.bundle_sid,
    bundle_status: v.bundle_status,
    status_as_of: jsonSafeValueToDate(v.status_as_of),
    status_reason: v.status_reason,
  }
  switch (v.purpose) {
    case 'CUSTOMER_PROFILE':
      return {...base, purpose: v.purpose, registered_name: v.registered_name}
    case 'CNAM':
      return {...base, purpose: v.purpose, cnam_display_name: v.cnam_display_name}
    case 'NUMBER_RENTAL':
      return {...base, purpose: v.purpose, number_rental_type: v.number_rental_type}
    case 'SHAKEN_STIR':
    case 'VOICE_INTEGRITY':
    case 'BRANDED_CALLS':
    case 'A2P_MESSAGING':
      return {...base, purpose: v.purpose}
    default:
      throw new TypeError(`Unrecognized registration purpose ${v.purpose} on ${base.bundle_sid}`)
  }
}

export enum RECORDING_STATUS {
  PROSPECT_RECORDED = 'prospect_recorded',
  PROSPECT_NOT_RECORDED = 'prospect_not_recorded',
}

export const RECORDING_STATUS_TO_HUMAN_READABLE: { [k in RECORDING_STATUS]: string } = {
  [RECORDING_STATUS.PROSPECT_RECORDED]: 'Prospect Recorded',
  [RECORDING_STATUS.PROSPECT_NOT_RECORDED]: 'Prospect Not Recorded',
}

export const SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE: { [k in SESSION_METRICS_COLUMNS]: string } = {
  [SESSION_METRICS_COLUMNS.SESSION_ID]: 'Session ID',
  [SESSION_METRICS_COLUMNS.USER_ID]: 'User ID',
  [SESSION_METRICS_COLUMNS.AUDIO_DURATION]: 'Audio Duration',
  [SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIPT_DURATION]: 'Prospect Transcript Duration',
  [SESSION_METRICS_COLUMNS.USER_DURATION]: 'User Duration',
  [SESSION_METRICS_COLUMNS.CALL_DURATION]: 'Call Duration',
  [SESSION_METRICS_COLUMNS.INITIAL_RINGING_DURATION]: 'Initial Ringing Duration',
  [SESSION_METRICS_COLUMNS.NUM_WORDS_PROSPECT]: 'Number of Words by Prospect',
  [SESSION_METRICS_COLUMNS.NUM_WORDS_REP]: 'Number of Words by Representative',
  [SESSION_METRICS_COLUMNS.LANGUAGE]: 'Language',
  [SESSION_METRICS_COLUMNS.IS_USER_HANGUP]: 'Is User Hangup',
  [SESSION_METRICS_COLUMNS.IS_AUTODIALER_CREATED]: 'Is Autodialer Created',
  [SESSION_METRICS_COLUMNS.IS_AUTODIALER_HANGUP]: 'Is Autodialer Hangup',
  [SESSION_METRICS_COLUMNS.IS_CONNECTED]: 'Is Connected',
  [SESSION_METRICS_COLUMNS.CAN_IMPROVE_EARLY_HANGUP]: 'Can Improve Early Hangup',
  [SESSION_METRICS_COLUMNS.CAN_IMPROVE_NAVIGATE_MENU]: 'Can Improve Navigate Menu',
  [SESSION_METRICS_COLUMNS.CONNECT_TIME]: 'Connect Time',
  [SESSION_METRICS_COLUMNS.CONNECT_IS_LIVE]: 'Connect Is Live',
  [SESSION_METRICS_COLUMNS.CONNECT_IS_RECORDING]: 'Connect Is Recording',
  [SESSION_METRICS_COLUMNS.IS_ANSWERED]: 'Is Answered',
  [SESSION_METRICS_COLUMNS.ANSWERED_TIME]: 'Answered Time',
  [SESSION_METRICS_COLUMNS.TARGET_DURATION]: 'Target Duration',
  [SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION]: 'Gatekeeper Duration',
  [SESSION_METRICS_COLUMNS.VOICEMAIL_DURATION]: 'Voicemail Duration',
  [SESSION_METRICS_COLUMNS.TARGET_SPEECH]: 'Target Speech',
  [SESSION_METRICS_COLUMNS.GATEKEEPER_SPEECH]: 'Gatekeeper Speech',
  [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_GATEKEEPER]: 'Rep Speech with Gatekeeper',
  [SESSION_METRICS_COLUMNS.LIVE_DURATION]: 'Live Duration',
  [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_TARGET]: 'Rep Speech with Target',
  [SESSION_METRICS_COLUMNS.REP_SPEECH_DURING_VOICEMAIL_DURATION]: 'Rep Speech During Voicemail Duration',
  [SESSION_METRICS_COLUMNS.LONGEST_MONOLOGUE_REP_TARGET]: 'Longest Monologue by Rep to Target',
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_ALL_ELIGIBLE]: 'Number of Words by Rep (All Eligible)',
  [SESSION_METRICS_COLUMNS.DURATION_REP_ALL_ELIGIBLE_SPEECH]: 'Duration of Rep Speech (All Eligible)',
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_ALL_ELIGIBLE]: 'Words per Second by Rep (All Eligible)',
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_TARGET_ELIGIBLE]: 'Number of Words by Target (Eligible)',
  [SESSION_METRICS_COLUMNS.DURATION_TARGET_ELIGIBLE_SPEECH]: 'Duration of Target Speech (Eligible)',
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_TARGET_ELIGIBLE]: 'Words per Second by Target (Eligible)',
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_TARGET_ELIGIBLE]: 'Number of Words by Rep to Target (Eligible)',
  [SESSION_METRICS_COLUMNS.DURATION_REP_TARGET_ELIGIBLE_SPEECH]: 'Duration of Rep Speech to Target (Eligible)',
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_TARGET_ELIGIBLE]: 'Words per Second by Rep to Target (Eligible)',
  [SESSION_METRICS_COLUMNS.R_DENY_COUNT]: 'Denial Count',
  [SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT]: 'Name Denial Count',
  [SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT]: 'Title Denial Count',
  [SESSION_METRICS_COLUMNS.R_TRANSFER_DENY_COUNT]: 'Transfer Denial Count',
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT]: 'Response: Not Decision Maker Count',
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT]: 'Response: Not Relevant Count',
  [SESSION_METRICS_COLUMNS.R_CALL_DENY_COUNT]: 'Call Denial Count',
  [SESSION_METRICS_COLUMNS.R_EMAIL_DENY_COUNT]: 'Email Denial Count',
  [SESSION_METRICS_COLUMNS.R_DELAYED_DENY_COUNT]: 'Delayed Denial Count',
  [SESSION_METRICS_COLUMNS.R_MEETING_DENY_COUNT]: 'Meeting Denial Count',
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT]: 'Product: No Need Count',
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT]: 'Product: No Budget Count',
  [SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT]: 'Competitors Using Count',
  [SESSION_METRICS_COLUMNS.C_ASKS_COUNT]: 'Asks Count',
  [SESSION_METRICS_COLUMNS.C_REFERRAL_ASK_COUNT]: 'Referral Ask Count',
  [SESSION_METRICS_COLUMNS.C_BUSINESS_ASK_COUNT]: 'Business Ask Count',
  [SESSION_METRICS_COLUMNS.C_RESP_CHECK_COUNT]: 'Response Check Count',
  [SESSION_METRICS_COLUMNS.C_MEETING_OFFER_COUNT]: 'Meeting Offer Count',
  [SESSION_METRICS_COLUMNS.C_CALL_OFFER_COUNT]: 'Call Offer Count',
  [SESSION_METRICS_COLUMNS.C_EMAIL_OFFER_COUNT]: 'Email Offer Count',
  [SESSION_METRICS_COLUMNS.R_MEETING_CONFIRM]: 'Meeting Confirmation',
  [SESSION_METRICS_COLUMNS.R_CALL_APPROVE]: 'Call Approval',
  [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE_COUNT]: 'Email Approval Count',
  [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE]: 'Email Approval',
  [SESSION_METRICS_COLUMNS.R_REFERRAL_PROVIDE]: 'Referral Provided',
  [SESSION_METRICS_COLUMNS.R_PRODUCT_INTERESTED_COUNT]: 'Product Interest Count',
  [SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT]: 'Generated Summary Text',
  [SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT]: 'Generated Email Text',
  [SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT]: 'Generated Coaching Text',
  [SESSION_METRICS_COLUMNS.STAGE]: 'Stage',
  [SESSION_METRICS_COLUMNS.STAGE_DISPOSITION]: 'Stage Disposition',
  [SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION]: 'Trellus Disposition',
  [SESSION_METRICS_COLUMNS.CUSTOM_DISPOSITION]: 'Custom Disposition',
  [SESSION_METRICS_COLUMNS.USER_DISPOSITION]: 'Disposition',
  [SESSION_METRICS_COLUMNS.USER_SENTIMENT]: 'Sentiment',
  [SESSION_METRICS_COLUMNS.USER_PURPOSE]: 'Purpose',
  [SESSION_METRICS_COLUMNS.PLATFORM]: 'Platform',
  [SESSION_METRICS_COLUMNS.BEST_COUNTERPART]: 'Best Counterpart',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_NUM_CALLS]: 'Prior Phone User: Number of Calls',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_MAX_ANSWERED_DURATION]: 'Prior Phone User: Max Answered Duration',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_FIRST]: 'Prior Phone User: Elapsed Time Since First',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_LAST]: 'Prior Phone User: Elapsed Time Since Last',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_IS_TEAM_FIRST]: 'Prior Phone User: Is Team First',
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_TEAM_NOT_USER_NUM_CALLS]: 'Prior Phone Team (Not User): Number of Calls',
  [SESSION_METRICS_COLUMNS.NEXT_C_CALL]: 'Calling someone else',
  [SESSION_METRICS_COLUMNS.NEXT_C_CALL_BACK]: 'Prospect call back',
  [SESSION_METRICS_COLUMNS.PROSPECT_NAME]: 'Prospect Name',
  [SESSION_METRICS_COLUMNS.PROSPECT_PHONE_VALUE]: 'Prospect Phone Value',
  [SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIBED]: 'Prospect Transcribed',
  [SESSION_METRICS_COLUMNS.PROSPECT_RECORDED]: 'Prospect Recorded',
  [SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIPT_PERSISTED]: 'Prospect Transcript Persisted',
  [SESSION_METRICS_COLUMNS.IS_FINALIZED]: "Is Finalized",
  [SESSION_METRICS_COLUMNS.HAS_COMMINGLED_MP3]: "Has Commingled MP3",
  [SESSION_METRICS_COLUMNS.SCHEDULED_START]: "Scheduled Start",
  [SESSION_METRICS_COLUMNS.CREATED_AT]: "Created At",
  [SESSION_METRICS_COLUMNS.TEAM_ID]: "Team Id",
  [SESSION_METRICS_COLUMNS.EXTERNAL_ACCOUNT_ID]: "External Account Id",
  [SESSION_METRICS_COLUMNS.ACCOUNT_ID]: "Account Id",
  [SESSION_METRICS_COLUMNS.USER_NAME]: "User Name",
  [SESSION_METRICS_COLUMNS.REP_PHONE_VALUE]: "Rep Phone Value",
  [SESSION_METRICS_COLUMNS.REP_PHONE_IS_LOCAL]: "Rep Phone Is Local",
}

export const PROSPECT_INFO_COLUMNS_TO_HUMAN_READABLE: { [k in PROSPECT_INFO_COLUMNS]: string } = {
  [PROSPECT_INFO_COLUMNS.SESSION_ID]: 'Session ID',
  [PROSPECT_INFO_COLUMNS.USER_ID]: 'User ID',
  [PROSPECT_INFO_COLUMNS.SCHEDULED_START]: 'Scheduled Start',
  [PROSPECT_INFO_COLUMNS.PLATFORM]: 'Platform',
  [PROSPECT_INFO_COLUMNS.PLATFORM_TENANT_ID]: 'Platform Tenant ID',
  [PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID]: 'Platform Person ID',
  [PROSPECT_INFO_COLUMNS.PLATFORM_COMPANY_ID]: 'Platform Company ID',
  [PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL]: 'Platform Person URL',
  [PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_VALUE]: 'Prospect Phone Value',
  [PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_TYPE]: 'Phone Type',
  [PROSPECT_INFO_COLUMNS.XREF_PLATFORM]: 'Cross-Reference Platform',
  [PROSPECT_INFO_COLUMNS.XREF_PERSON_ID]: 'Cross-Reference Person ID',
  [PROSPECT_INFO_COLUMNS.XREF_COMPANY_ID]: 'Cross-Reference Company ID',
  [PROSPECT_INFO_COLUMNS.XREF_PERSON_URL]: 'Cross-Reference Person URL',
  [PROSPECT_INFO_COLUMNS.CADENCE]: 'Cadence',
  [PROSPECT_INFO_COLUMNS.CADENCE_2]: 'Cadence 2',
  [PROSPECT_INFO_COLUMNS.CADENCE_3]: 'Cadence 3',
  [PROSPECT_INFO_COLUMNS.CADENCE_STEP]: "Cadence Step",
  [PROSPECT_INFO_COLUMNS.CADENCE_STEP_2]: 'Cadence Step 2',
  [PROSPECT_INFO_COLUMNS.CADENCE_STEP_3]: 'Cadence Step 3',
  [PROSPECT_INFO_COLUMNS.PROSPECT_NAME]: 'Prospect Name',
  [PROSPECT_INFO_COLUMNS.PROSPECT_TITLE]: 'Prospect Title',
  [PROSPECT_INFO_COLUMNS.PROSPECT_OCCUPATION]: 'Prospect Occupation',
  [PROSPECT_INFO_COLUMNS.PROSPECT_SENIORITY]: 'Prospect Seniority',
  [PROSPECT_INFO_COLUMNS.PROSPECT_LOCATION]: 'Prospect Location',
  [PROSPECT_INFO_COLUMNS.PROSPECT_LINKEDIN]: 'Prospect LinkedIn',
  [PROSPECT_INFO_COLUMNS.PROSPECT_EMAIL]: 'Prospect Email',
  [PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_OTHER_1]: 'Prospect Phone (Other 1)',
  [PROSPECT_INFO_COLUMNS.PROSPECT_PHONE_OTHER_2]: 'Prospect Phone (Other 2)',
  [PROSPECT_INFO_COLUMNS.COMPANY_NAME]: 'Company Name',
  [PROSPECT_INFO_COLUMNS.COMPANY_LINKEDIN]: 'Company LinkedIn',
  [PROSPECT_INFO_COLUMNS.COMPANY_URL]: 'Company URL',
  [PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY]: 'Company Industry',
  [PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY_2]: 'Company Industry 2',
  [PROSPECT_INFO_COLUMNS.COMPANY_INDUSTRY_3]: 'Company Industry 3',
  [PROSPECT_INFO_COLUMNS.COMPANY_SIZE_MIN]: 'Company Size (Min)',
  [PROSPECT_INFO_COLUMNS.COMPANY_SIZE_MAX]: 'Company Size (Max)',
  [PROSPECT_INFO_COLUMNS.COMPANY_PERSONA]: 'Company Persona',
  [PROSPECT_INFO_COLUMNS.PROSPECT_LIST]: 'Prospect List',
  [PROSPECT_INFO_COLUMNS.PROSPECT_LIST_2]: 'Prospect List 2',
  [PROSPECT_INFO_COLUMNS.PROSPECT_LIST_3]: 'Prospect List 3',
  [PROSPECT_INFO_COLUMNS.MARKET]: 'Market',
  [PROSPECT_INFO_COLUMNS.LEAD_TYPE]: 'Lead Type',
};

export type AIManyCallAnalysis = {
  user_id: string
  team_id: string
  period_start: Date
  period_end: Date
  transcripts_analyzed: string
  analysis_raw: string
  analysis: string
}

export function jsonSafeToAIManyCallAnalysis(v: Record<string, any>): AIManyCallAnalysis {
  return {
    user_id: v.user_id,
    team_id: v.team_id,
    period_start: jsonSafeValueToDate(v.period_start),
    period_end: jsonSafeValueToDate(v.period_end),
    transcripts_analyzed: v.transcripts_analyzed,
    analysis_raw: v.analysis_raw,
    analysis: v.analysis,
  }
}

export function jsonSafeToMultiAICallAnalysis(v: Record<string, any>): AIManyCallAnalysis[] {
  return v.map((x: Record<string, any>) => jsonSafeToAIManyCallAnalysis(x))
}

export enum TwilioComplianceStatus {
  ABSENT = 'absent',
  DRAFT = 'draft',
  PENDING_REVIEW = 'pending-review',
  IN_REVIEW = 'in-review',
  TWILIO_REJECTED = 'twilio-rejected',
  TWILIO_APPROVED = 'twilio-approved',
}

export type TwilioBusinessProfile = {
  twilio_account_sid: string
  team_id: string
  customer_profile_sid: string
  customer_profile_status: TwilioComplianceStatus
  shaken_stir_trust_product_sid: string
  shaken_stir_trust_product_status: TwilioComplianceStatus
  voice_integrity_trust_product_sid: string | null
  voice_integrity_trust_product_status: TwilioComplianceStatus | null
}

export function jsonSafeToTwilioBusinessProfile(v: Record<string, any>): TwilioBusinessProfile {
  return {
    twilio_account_sid: v.twilio_account_sid,
    team_id: v.team_id,
    customer_profile_sid: v.customer_profile_sid,
    customer_profile_status: v.customer_profile_status,
    shaken_stir_trust_product_sid: v.shaken_stir_trust_product_sid,
    shaken_stir_trust_product_status: v.shaken_stir_trust_product_status,
    voice_integrity_trust_product_sid: v.voice_integrity_trust_product_sid ?? null,
    voice_integrity_trust_product_status: v.voice_integrity_trust_product_status ?? null,
  }
}

export type TwilioRegulatoryBundleRule = {
  twilio_account_sid: string
  team_id: string
  rule_id: string
  user_id: string | null
  country: string // 2-letter country code, all caps
  number_type: string
  bundle_sid: string
  address_sid: string | null
}

export function jsonSafeToTwilioRegulatoryBundleRule(v: Record<string, any>): TwilioRegulatoryBundleRule {
  return {
    twilio_account_sid: v.twilio_account_sid,
    team_id: v.team_id,
    rule_id: v.rule_id,
    user_id: v.user_id ?? null,
    country: v.country,
    number_type: v.number_type,
    bundle_sid: v.bundle_sid,
    address_sid: v.address_sid ?? null,
  }
}

export type RecordedGreeting = {
  recorded_greeting_id: string
  user_id: string
  name: string
  file_size: number  // size in bytes
}

export type SalesRoom = {
  room_id: string
  room_name: string
  team_id: string | null
  user_id: string | null
  is_personal: boolean
}

export function jsonSafeToSalesRoom(v: Record<string, any>): SalesRoom {
  return {
    room_id: v.room_id,
    room_name: v.room_name,
    team_id: v.team_id,
    user_id: v.user_id,
    is_personal: v.is_personal,
  }
}

export type TeamInvitation = {
  team_id: string
  team_name: string | null
  email: string
  expires_at: Date
  can_dial: boolean
}

export function jsonSafeToTeamInvitation(v: Record<string, any>): TeamInvitation {
  return {
    team_id: v.team_id,
    team_name: v.team_name ?? null,
    email: v.email,
    expires_at: jsonSafeValueToDate(v.expires_at),
    can_dial: v.can_dial,
  }
}

export type BotSkill = {
  bot_skill_id: string
  skill_name: string
  team_id: string
  user_id: string | null
  is_global: boolean
  prior_context: string
  first_phrases: string
  rep_positive: string
  rep_negative: string
  outcome_positive: string
  outcome_negative: string
}

export enum BotStyle {
  COLD_CALL = 'COLD_CALL',
  FOLLOW_UP_CALL = 'FOLLOW_UP_CALL',
  REFERRAL_CALL = 'REFERRAL_CALL',
  WARM_CALL = 'WARM_CALL'
}


export enum BotLanguage {
  ENGLISH = 'ENGLISH',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH'
}

export enum Language {
  EN_US = 'EN_US',
  ES = 'ES',
  ES_LATIN = 'ES_LATIN',
  FRENCH = 'FRENCH',
  MULTI = 'MULTI'
}

export enum CallBotEmotion {
  NEUTRAL = 'NEUTRAL',
  HAPPY = 'HAPPY',
  SAD = 'SAD',
  ANGRY = 'ANGRY',
  EXCITED = 'EXCITED',
  NERVOUS = 'NERVOUS',
  CURIOUS = 'CURIOUS',
  FRUSTRATED = 'FRUSTRATED',
  CALM = 'CALM',
  EMPATHETIC = 'EMPATHETIC',
  CONFIDENT = 'CONFIDENT',
  DOUBTFUL = 'DOUBTFUL',
  APOLOGETIC = 'APOLOGETIC',
  SKEPTICAL = 'SKEPTICAL',
  OPTIMISTIC = 'OPTIMISTIC',
  PESSIMISTIC = 'PESSIMISTIC',
  SARCASTIC = 'SARCASTIC',
  INDIFFERENT = 'INDIFFERENT',
  URGENT = 'URGENT',
  RELAXED = 'RELAXED'
}

export enum BotVoiceType {
  YOUNG_MAN = 'YOUNG_MAN',
  YOUNG_WOMAN = 'YOUNG_WOMAN',
  MIDDLE_AGED_MAN = 'MIDDLE_AGED_MAN',
  MIDDLE_AGED_WOMAN = 'MIDDLE_AGED_WOMAN',
  OLD_MAN = 'OLD_MAN',
  OLD_WOMAN = 'OLD_WOMAN',
  AUTHORITATIVE_MAN = 'AUTHORITATIVE_MAN',
  AUTHORITATIVE_WOMAN = 'AUTHORITATIVE_WOMAN',
  CALM_MAN = 'CALM_MAN',
  CALM_WOMAN = 'CALM_WOMAN'
}

export type BotCallTranscript = {
  is_rep: boolean
  text: string
  timestamp: number
  duration: number // in seconds
}

export type BotCallAnalysis = {
  opener: {
    politeness_and_professionalism: number
    clarity_and_conciseness: number
    engagement: number,
    personalization: number,
    tone_and_enthusiasm: number,
  },
  qualification: {
    thorough_assessment: number,
    open_ended_questions: number,
    listening_skills: number,
    relevance: number,
    building_rapport: number,
  }, 
  objection_handling: {
    empathy_and_understanding: number,
    clarification: number,
    solution_oriented_responses: number,
    persistence_without_pressure: number,
    case_studies: number,
  }, 
  closing: {
    clear_next_steps: number,
    closing_technique: number,
    confirmation: number,
    politeness_and_sincerity: number,
    follow_up_indication: number,
  }
  overall_evaluation: {
    total_score: number
    overall_commentary: string
  },
}

export const BOT_CALL_ANALYSIS_VERSION = 'bot-analysis-model-v1'

export enum CallLanguage {
  ENGLISH = 'ENGLISH',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH',
  DUTCH = 'DUTCH',
  GERMAN = 'GERMAN'
}

export enum CallDifficultyMode {
  EASY = 'EASY',
  MEDIUM = 'MEDIUM',
  HARD = 'HARD'
}

export const CALL_DIFFICULTY_TO_HUMAN_READABLE: {[k in CallDifficultyMode]: string} = {
  [CallDifficultyMode.EASY]: 'Easy',
  [CallDifficultyMode.MEDIUM]: 'Medium',
  [CallDifficultyMode.HARD]: 'Hard'
}

export type BotCall = {
  bot_call_id: string
  bot_id: string
  user_id: string
  team_id: string
  start: Date
  call_score: number | null
  duration: number | null
  call_transcripts: BotCallTranscript[] | null
  call_analysis: BotCallAnalysis | null
  call_analysis_version: string | null
  audio_url: string | null
  difficulty_mode?: CallDifficultyMode // Add this field
  searched?: boolean | null
  isAnalyzing?: boolean | null
}

export function getCallTranscriptFromString(s: string | null): BotCallTranscript[] {
  if (!s) return []
  try {
    const parsed = JSON.parse(s)
    return parsed.filter((x: any) => x['role'] && x['role'] !== 'system')
      .map((x: any) => ({
        is_rep: x['role'] === 'user',
        text: x.message,
        timestamp: x.secondsFromStart,
        duration: (x['endTime'] - x['time']) / 1000
      }))
  } catch (e) {
    console.error('Error parsing call transcript string', e)
    return []
  }
}

export function jsonSafeToBotCallAnalysis(v: Record<string, any>): BotCallAnalysis | null {
  if (v === null) return null
  try {
    return {
      opener: {
        politeness_and_professionalism: v.opener.politeness_and_professionalism,
      clarity_and_conciseness: v.opener.clarity_and_conciseness,
      engagement: v.opener.engagement,
      personalization: v.opener.personalization,
      tone_and_enthusiasm: v.opener.tone_and_enthusiasm,
    },
    qualification: {
      thorough_assessment: v.qualification.thorough_assessment,
      open_ended_questions: v.qualification.open_ended_questions,
      listening_skills: v.qualification.listening_skills,
      relevance: v.qualification.relevance,
      building_rapport: v.qualification.building_rapport,
    },
    objection_handling: {
      empathy_and_understanding: v.objection_handling.empathy_and_understanding,
      clarification: v.objection_handling.clarification,
      solution_oriented_responses: v.objection_handling.solution_oriented_responses,
      persistence_without_pressure: v.objection_handling.persistence_without_pressure,
      case_studies: v.objection_handling.case_studies,
    },
    closing: {
      clear_next_steps: v.closing.clear_next_steps,
      closing_technique: v.closing.closing_technique,
      confirmation: v.closing.confirmation,
      politeness_and_sincerity: v.closing.politeness_and_sincerity,
      follow_up_indication: v.closing.follow_up_indication,
    },
    overall_evaluation: {
      total_score: v.overall_evaluation.total_score,
        overall_commentary: v.overall_evaluation.overall_commentary,
      }
    }
  } catch (e) {
    console.error('Error parsing bot call analysis', e)
    return null
  }
}

export function jsonSafeToBotCall(v: Record<string, any>): BotCall {
  return {
    bot_call_id: v.bot_call_id,
    bot_id: v.bot_id,
    user_id: v.user_id,
    team_id: v.team_id,
    start: jsonSafeValueToDate(v.start),
    call_score: v.call_score === -1 ? null : v.call_score,
    call_transcripts: getCallTranscriptFromString(v.call_transcripts),
    call_analysis: v.call_analysis_version === BOT_CALL_ANALYSIS_VERSION ? jsonSafeToBotCallAnalysis(JSON.parse(v.call_analysis)) : null,
    call_analysis_version: v.call_analysis_version,
    duration: v.duration,
    audio_url: v.audio_url,
    difficulty_mode: v.difficulty_level || CallDifficultyMode.EASY, // Default to EASY
  }
}

export function jsonSafeToBotCalls(v: Record<string, any>): BotCall[] {
  try {
    return v.map((x: Record<string, any>) => jsonSafeToBotCall(x))
  } catch (e) {
    console.error('Error parsing bot calls', e)
    return []
  }
}

export type BotGeneration = {
  meta_description: string
  background: string
  problems: string
  objections: string
  custom_instructions: string
  is_male: boolean
}

export type Bot = {
  bot_id: string
  team_id: string
  name: string
  meta_description: string
  background_instruction: string
  problem_instruction: string
  objections_instruction: string
  bot_language: BotLanguage
  bot_style: BotStyle
  voice: BotVoiceType
  is_active: boolean
  is_global: boolean
  user_id: string | null
  custom_instructions: string | null
  updated_at: string
  tags: string[] | null
  xref_profile_url: string | null
  xref_prospect_id: string | null
}

export function jsonSafeToBot(v: Record<string, any>): Bot {
  return {
    bot_id: v.bot_id,
    team_id: v.team_id,
    name: v.name,
    meta_description: v.meta_description,
    background_instruction: v.background_instruction,
    problem_instruction: v.problem_instruction,
    objections_instruction: v.objections_instruction,
    bot_language: v.bot_language,
    bot_style: v.bot_style,
    voice: v.voice,
    is_active: v.is_active,
    is_global: v.is_global,
    user_id: v.user_id,
    custom_instructions: v.custom_instructions,
    updated_at: jsonSafeValueToDate(v.updated_at).toISOString(),
    tags: v.tags ? v.tags.split(',') : null,
    xref_profile_url: v.xref_profile_url,
    xref_prospect_id: v.xref_prospect_id
  }
}

export function jsonSafeToBots(v: Record<string, any>): Bot[] {
  return v.map((x: Record<string, any>) => jsonSafeToBot(x))
}

export type BotEmotion = {
  bot_id: string
  emotion: CallBotEmotion
}

// Add this near other enums/mappings
export const BotVoiceToAvatarUrl: Record<BotVoiceType, string> = {
  [BotVoiceType.YOUNG_MAN]: '/avatars/young-man.png',
  [BotVoiceType.YOUNG_WOMAN]: '/avatars/young-woman.png',
  [BotVoiceType.MIDDLE_AGED_MAN]: '/avatars/middle-aged-man.png',
  [BotVoiceType.MIDDLE_AGED_WOMAN]: '/avatars/middle-aged-woman.png',
  [BotVoiceType.OLD_MAN]: '/avatars/old-man.png',
  [BotVoiceType.OLD_WOMAN]: '/avatars/old-woman.png',
  [BotVoiceType.AUTHORITATIVE_MAN]: '/avatars/authoritative-man.png',
  [BotVoiceType.AUTHORITATIVE_WOMAN]: '/avatars/authoritative-woman.png',
  [BotVoiceType.CALM_MAN]: '/avatars/calm-man.png',
  [BotVoiceType.CALM_WOMAN]: '/avatars/calm-woman.png'
}

export enum CustomMetricSource {
  GEN_AI = 'GEN_AI',
  PHRASE = 'PHRASE',
  AVERAGE = 'AVERAGE',
  CODE = 'CODE'
}

export enum CustomMetricValueType {
  BOOLEAN = 'BOOLEAN',
  PERCENT = 'PERCENT',
  TEXT = 'TEXT',
  ENUM = 'ENUM'
}

export type CustomDefinition = {
  custom_definition_id: string
  team_id: string
  metric_idx: number | null
  label: string | null
  description: string | null
  order: number | null
  tags: string | null
  value_source: CustomMetricSource
  value_type: CustomMetricValueType
  value_type_enum_str: string | null
  value_gen_str: string | null
  value_gen_desc_str: string | null
  value_phrase_str: string | null
  value_phrase_role: PartyRole | null
  value_average_str: string | null
  value_code_str: string | null

  where_min_duration: number | null
  where_max_duration: number | null
  where_gen_str: string | null

  modified_as_of: Date | null
  rebuilt_as_of: Date | null
}

export function jsonSafeToCustomDefinition(v: Record<string, any>): CustomDefinition {
  return {
    custom_definition_id: v.custom_definition_id,
    team_id: v.team_id,
    metric_idx: v.metric_idx,
    label: v.label,
    description: v.description,
    order: v.order,
    tags: v.tags,
    value_source: v.value_source,
    value_type: v.value_type,
    value_type_enum_str: v.value_type_enum_str,
    value_gen_str: v.value_gen_str,
    value_gen_desc_str: v.value_gen_desc_str,
    value_phrase_str: v.value_phrase_str,
    value_phrase_role: v.value_phrase_role,
    value_average_str: v.value_average_str,
    value_code_str: v.value_code_str,
    where_min_duration: v.where_min_duration,
    where_max_duration: v.where_max_duration,
    where_gen_str: v.where_gen_str,
    modified_as_of: jsonSafeValueToDate(v.modified_as_of),
    rebuilt_as_of: v.rebuilt_as_of ? jsonSafeValueToDate(v.rebuilt_as_of) : null,
  }
}

export function jsonSafeToCustomDefinitions(v: Record<string, any>): CustomDefinition[] {
  return v.map((x: Record<string, any>) => jsonSafeToCustomDefinition(x))
}



export type CustomMetric = {
  session_id: string
  team_id: string
  scheduled_start: Date
  calculated_as_of: Date
  metric_version: string
  metric_0: number | null
  metric_1: number | null
  metric_2: number | null
  metric_3: number | null
  metric_4: number | null
  metric_5: number | null
  metric_6: number | null
  metric_7: number | null
  metric_8: number | null
  metric_9: number | null
  metric_10: number | null
  metric_11: number | null
  metric_12: number | null
  metric_13: number | null
  metric_14: number | null
  metric_15: number | null
  metric_16: number | null
  metric_17: number | null
  metric_18: number | null
  metric_19: number | null
  text_0: string | null
  text_1: string | null
  text_2: string | null
  text_3: string | null
  text_4: string | null
}

export function jsonSafeToCustomMetric(v: Record<string, any>): CustomMetric {
  return {
    session_id: v.session_id,
    team_id: v.team_id,
    scheduled_start: jsonSafeValueToDate(v.scheduled_start),
    calculated_as_of: jsonSafeValueToDate(v.calculated_as_of),
    metric_version: v.metric_version,
    metric_0: v.metric_0,
    metric_1: v.metric_1,
    metric_2: v.metric_2,
    metric_3: v.metric_3,
    metric_4: v.metric_4,
    metric_5: v.metric_5,
    metric_6: v.metric_6,
    metric_7: v.metric_7,
    metric_8: v.metric_8,
    metric_9: v.metric_9,
    metric_10: v.metric_10,
    metric_11: v.metric_11,
    metric_12: v.metric_12,
    metric_13: v.metric_13,
    metric_14: v.metric_14,
    metric_15: v.metric_15,
    metric_16: v.metric_16,
    metric_17: v.metric_17,
    metric_18: v.metric_18,
    metric_19: v.metric_19,
    text_0: v.text_0,
    text_1: v.text_1,
    text_2: v.text_2,
    text_3: v.text_3,
    text_4: v.text_4
  }
}


export type CustomDetail = {
  session_id: string
  team_id: string
  scheduled_start: Date
  calculated_as_of: Date
  metric_to_raw_json_str: string | null
  metric_to_time_json_str: string | null
  metric_to_explanation_json_str: string | null
}

export function jsonSafeToCustomDetail(v: Record<string, any>): CustomDetail {
  return {
    session_id: v.session_id,
    team_id: v.team_id,
    scheduled_start: jsonSafeValueToDate(v.scheduled_start),
    calculated_as_of: jsonSafeValueToDate(v.calculated_as_of),
    metric_to_raw_json_str: v.metric_to_raw_json_str,
    metric_to_time_json_str: v.metric_to_time_json_str,
    metric_to_explanation_json_str: v.metric_to_explanation_json_str
  }
}

export type SessionMetric = {
  [SESSION_METRICS_COLUMNS.SESSION_ID]: string
  [SESSION_METRICS_COLUMNS.USER_ID]: string
  [SESSION_METRICS_COLUMNS.AUDIO_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIPT_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.USER_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.CALL_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.INITIAL_RINGING_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.NUM_WORDS_PROSPECT]: number | null
  [SESSION_METRICS_COLUMNS.NUM_WORDS_REP]: number | null
  [SESSION_METRICS_COLUMNS.LANGUAGE]: string | null
  [SESSION_METRICS_COLUMNS.IS_USER_HANGUP]: boolean | null
  [SESSION_METRICS_COLUMNS.IS_AUTODIALER_CREATED]: boolean | null
  [SESSION_METRICS_COLUMNS.IS_AUTODIALER_HANGUP]: boolean | null
  [SESSION_METRICS_COLUMNS.IS_CONNECTED]: boolean | null
  [SESSION_METRICS_COLUMNS.CAN_IMPROVE_EARLY_HANGUP]: boolean | null
  [SESSION_METRICS_COLUMNS.CAN_IMPROVE_NAVIGATE_MENU]: boolean | null
  [SESSION_METRICS_COLUMNS.CONNECT_TIME]: number | null
  [SESSION_METRICS_COLUMNS.CONNECT_IS_LIVE]: boolean | null
  [SESSION_METRICS_COLUMNS.CONNECT_IS_RECORDING]: boolean | null
  [SESSION_METRICS_COLUMNS.IS_ANSWERED]: boolean | null
  [SESSION_METRICS_COLUMNS.ANSWERED_TIME]: number | null
  [SESSION_METRICS_COLUMNS.TARGET_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.VOICEMAIL_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.TARGET_SPEECH]: number | null
  [SESSION_METRICS_COLUMNS.LIVE_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.GATEKEEPER_SPEECH]: number | null
  [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_GATEKEEPER]: number | null
  [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_TARGET]: number | null
  [SESSION_METRICS_COLUMNS.REP_SPEECH_DURING_VOICEMAIL_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.LONGEST_MONOLOGUE_REP_TARGET]: number | null
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_ALL_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.DURATION_REP_ALL_ELIGIBLE_SPEECH]: number | null
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_ALL_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_TARGET_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.DURATION_TARGET_ELIGIBLE_SPEECH]: number | null
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_TARGET_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_TARGET_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.DURATION_REP_TARGET_ELIGIBLE_SPEECH]: number | null
  [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_TARGET_ELIGIBLE]: number | null
  [SESSION_METRICS_COLUMNS.R_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_TRANSFER_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_CALL_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_EMAIL_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_DELAYED_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_MEETING_DENY_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_ASKS_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_REFERRAL_ASK_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_BUSINESS_ASK_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_RESP_CHECK_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_MEETING_OFFER_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_CALL_OFFER_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.C_EMAIL_OFFER_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_MEETING_CONFIRM]: number | null
  [SESSION_METRICS_COLUMNS.R_CALL_APPROVE]: number | null
  [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE]: number | null
  [SESSION_METRICS_COLUMNS.R_REFERRAL_PROVIDE]: number | null
  [SESSION_METRICS_COLUMNS.R_PRODUCT_INTERESTED_COUNT]: number | null
  [SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT]: string | null
  [SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT]: string | null
  [SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT]: string | null
  [SESSION_METRICS_COLUMNS.STAGE]: string | null
  [SESSION_METRICS_COLUMNS.STAGE_DISPOSITION]: string | null
  [SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION]: string | null
  [SESSION_METRICS_COLUMNS.CUSTOM_DISPOSITION]: string | null
  [SESSION_METRICS_COLUMNS.USER_DISPOSITION]: string | null
  [SESSION_METRICS_COLUMNS.USER_SENTIMENT]: string | null
  [SESSION_METRICS_COLUMNS.USER_PURPOSE]: string | null
  [SESSION_METRICS_COLUMNS.PLATFORM]: Platform | null
  [SESSION_METRICS_COLUMNS.BEST_COUNTERPART]: Counterpart | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_NUM_CALLS]: number | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_MAX_ANSWERED_DURATION]: number | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_FIRST]: number | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_LAST]: number | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_IS_TEAM_FIRST]: boolean | null
  [SESSION_METRICS_COLUMNS.PRIOR_PHONE_TEAM_NOT_USER_NUM_CALLS]: number | null
  [SESSION_METRICS_COLUMNS.NEXT_C_CALL]: boolean | null
  [SESSION_METRICS_COLUMNS.NEXT_C_CALL_BACK]: boolean | null
}

export function jsonSafeToSessionMetric(v: Record<string, any>): SessionMetric {
  return {
    [SESSION_METRICS_COLUMNS.SESSION_ID]: v[SESSION_METRICS_COLUMNS.SESSION_ID],
    [SESSION_METRICS_COLUMNS.USER_ID]: v[SESSION_METRICS_COLUMNS.USER_ID],
    [SESSION_METRICS_COLUMNS.AUDIO_DURATION]: v[SESSION_METRICS_COLUMNS.AUDIO_DURATION],
    [SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIPT_DURATION]: v[SESSION_METRICS_COLUMNS.PROSPECT_TRANSCRIPT_DURATION],
    [SESSION_METRICS_COLUMNS.USER_DURATION]: v[SESSION_METRICS_COLUMNS.USER_DURATION],
    [SESSION_METRICS_COLUMNS.CALL_DURATION]: v[SESSION_METRICS_COLUMNS.CALL_DURATION],
    [SESSION_METRICS_COLUMNS.INITIAL_RINGING_DURATION]: v[SESSION_METRICS_COLUMNS.INITIAL_RINGING_DURATION],
    [SESSION_METRICS_COLUMNS.NUM_WORDS_PROSPECT]: v[SESSION_METRICS_COLUMNS.NUM_WORDS_PROSPECT],
    [SESSION_METRICS_COLUMNS.NUM_WORDS_REP]: v[SESSION_METRICS_COLUMNS.NUM_WORDS_REP],
    [SESSION_METRICS_COLUMNS.LANGUAGE]: v[SESSION_METRICS_COLUMNS.LANGUAGE],
    [SESSION_METRICS_COLUMNS.IS_USER_HANGUP]: v[SESSION_METRICS_COLUMNS.IS_USER_HANGUP],
    [SESSION_METRICS_COLUMNS.IS_AUTODIALER_CREATED]: v[SESSION_METRICS_COLUMNS.IS_AUTODIALER_CREATED],
    [SESSION_METRICS_COLUMNS.IS_AUTODIALER_HANGUP]: v[SESSION_METRICS_COLUMNS.IS_AUTODIALER_HANGUP],
    [SESSION_METRICS_COLUMNS.IS_CONNECTED]: v[SESSION_METRICS_COLUMNS.IS_CONNECTED],
    [SESSION_METRICS_COLUMNS.CAN_IMPROVE_EARLY_HANGUP]: v[SESSION_METRICS_COLUMNS.CAN_IMPROVE_EARLY_HANGUP],
    [SESSION_METRICS_COLUMNS.CAN_IMPROVE_NAVIGATE_MENU]: v[SESSION_METRICS_COLUMNS.CAN_IMPROVE_NAVIGATE_MENU],
    [SESSION_METRICS_COLUMNS.CONNECT_TIME]: v[SESSION_METRICS_COLUMNS.CONNECT_TIME],
    [SESSION_METRICS_COLUMNS.CONNECT_IS_LIVE]: v[SESSION_METRICS_COLUMNS.CONNECT_IS_LIVE],
    [SESSION_METRICS_COLUMNS.CONNECT_IS_RECORDING]: v[SESSION_METRICS_COLUMNS.CONNECT_IS_RECORDING],
    [SESSION_METRICS_COLUMNS.IS_ANSWERED]: v[SESSION_METRICS_COLUMNS.IS_ANSWERED],
    [SESSION_METRICS_COLUMNS.ANSWERED_TIME]: v[SESSION_METRICS_COLUMNS.ANSWERED_TIME],
    [SESSION_METRICS_COLUMNS.TARGET_DURATION]: v[SESSION_METRICS_COLUMNS.TARGET_DURATION],
    [SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION]: v[SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION],
    [SESSION_METRICS_COLUMNS.VOICEMAIL_DURATION]: v[SESSION_METRICS_COLUMNS.VOICEMAIL_DURATION],
    [SESSION_METRICS_COLUMNS.TARGET_SPEECH]: v[SESSION_METRICS_COLUMNS.TARGET_SPEECH],
    [SESSION_METRICS_COLUMNS.LIVE_DURATION]: v[SESSION_METRICS_COLUMNS.LIVE_DURATION],
    [SESSION_METRICS_COLUMNS.GATEKEEPER_SPEECH]: v[SESSION_METRICS_COLUMNS.GATEKEEPER_SPEECH],
    [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_GATEKEEPER]: v[SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_GATEKEEPER],
    [SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_TARGET]: v[SESSION_METRICS_COLUMNS.REP_SPEECH_WITH_TARGET],
    [SESSION_METRICS_COLUMNS.REP_SPEECH_DURING_VOICEMAIL_DURATION]: v[SESSION_METRICS_COLUMNS.REP_SPEECH_DURING_VOICEMAIL_DURATION],
    [SESSION_METRICS_COLUMNS.LONGEST_MONOLOGUE_REP_TARGET]: v[SESSION_METRICS_COLUMNS.LONGEST_MONOLOGUE_REP_TARGET],
    [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_ALL_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_ALL_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.DURATION_REP_ALL_ELIGIBLE_SPEECH]: v[SESSION_METRICS_COLUMNS.DURATION_REP_ALL_ELIGIBLE_SPEECH],
    [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_ALL_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_ALL_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.NUMBER_WORDS_TARGET_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.NUMBER_WORDS_TARGET_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.DURATION_TARGET_ELIGIBLE_SPEECH]: v[SESSION_METRICS_COLUMNS.DURATION_TARGET_ELIGIBLE_SPEECH],
    [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_TARGET_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_TARGET_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_TARGET_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.NUMBER_WORDS_REP_TARGET_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.DURATION_REP_TARGET_ELIGIBLE_SPEECH]: v[SESSION_METRICS_COLUMNS.DURATION_REP_TARGET_ELIGIBLE_SPEECH],
    [SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_TARGET_ELIGIBLE]: v[SESSION_METRICS_COLUMNS.WORDS_PER_SECOND_REP_TARGET_ELIGIBLE],
    [SESSION_METRICS_COLUMNS.R_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_TRANSFER_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_TRANSFER_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT]: v[SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT],
    [SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT]: v[SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT],
    [SESSION_METRICS_COLUMNS.R_CALL_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_CALL_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_EMAIL_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_EMAIL_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_DELAYED_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_DELAYED_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_MEETING_DENY_COUNT]: v[SESSION_METRICS_COLUMNS.R_MEETING_DENY_COUNT],
    [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT]: v[SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT],
    [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT]: v[SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT],
    [SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT]: v[SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT],
    [SESSION_METRICS_COLUMNS.C_ASKS_COUNT]: v[SESSION_METRICS_COLUMNS.C_ASKS_COUNT],
    [SESSION_METRICS_COLUMNS.C_REFERRAL_ASK_COUNT]: v[SESSION_METRICS_COLUMNS.C_REFERRAL_ASK_COUNT],
    [SESSION_METRICS_COLUMNS.C_BUSINESS_ASK_COUNT]: v[SESSION_METRICS_COLUMNS.C_BUSINESS_ASK_COUNT],
    [SESSION_METRICS_COLUMNS.C_RESP_CHECK_COUNT]: v[SESSION_METRICS_COLUMNS.C_RESP_CHECK_COUNT],
    [SESSION_METRICS_COLUMNS.C_MEETING_OFFER_COUNT]: v[SESSION_METRICS_COLUMNS.C_MEETING_OFFER_COUNT],
    [SESSION_METRICS_COLUMNS.C_CALL_OFFER_COUNT]: v[SESSION_METRICS_COLUMNS.C_CALL_OFFER_COUNT],
    [SESSION_METRICS_COLUMNS.C_EMAIL_OFFER_COUNT]: v[SESSION_METRICS_COLUMNS.C_EMAIL_OFFER_COUNT],
    [SESSION_METRICS_COLUMNS.R_MEETING_CONFIRM]: v[SESSION_METRICS_COLUMNS.R_MEETING_CONFIRM],
    [SESSION_METRICS_COLUMNS.R_CALL_APPROVE]: v[SESSION_METRICS_COLUMNS.R_CALL_APPROVE],
    [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE_COUNT]: v[SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE_COUNT],
    [SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE]: v[SESSION_METRICS_COLUMNS.R_EMAIL_APPROVE],
    [SESSION_METRICS_COLUMNS.R_REFERRAL_PROVIDE]: v[SESSION_METRICS_COLUMNS.R_REFERRAL_PROVIDE],
    [SESSION_METRICS_COLUMNS.R_PRODUCT_INTERESTED_COUNT]: v[SESSION_METRICS_COLUMNS.R_PRODUCT_INTERESTED_COUNT],
    [SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT]: v[SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT],
    [SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT]: v[SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT],
    [SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT]: v[SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT],
    [SESSION_METRICS_COLUMNS.STAGE]: v[SESSION_METRICS_COLUMNS.STAGE],
    [SESSION_METRICS_COLUMNS.STAGE_DISPOSITION]: v[SESSION_METRICS_COLUMNS.STAGE_DISPOSITION],
    [SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION]: v[SESSION_METRICS_COLUMNS.TRELLUS_DISPOSITION],
    [SESSION_METRICS_COLUMNS.CUSTOM_DISPOSITION]: v[SESSION_METRICS_COLUMNS.CUSTOM_DISPOSITION],
    [SESSION_METRICS_COLUMNS.USER_DISPOSITION]: v[SESSION_METRICS_COLUMNS.USER_DISPOSITION],
    [SESSION_METRICS_COLUMNS.USER_SENTIMENT]: v[SESSION_METRICS_COLUMNS.USER_SENTIMENT],
    [SESSION_METRICS_COLUMNS.USER_PURPOSE]: v[SESSION_METRICS_COLUMNS.USER_PURPOSE],
    [SESSION_METRICS_COLUMNS.PLATFORM]: v[SESSION_METRICS_COLUMNS.PLATFORM],
    [SESSION_METRICS_COLUMNS.BEST_COUNTERPART]: v[SESSION_METRICS_COLUMNS.BEST_COUNTERPART],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_NUM_CALLS]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_NUM_CALLS],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_MAX_ANSWERED_DURATION]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_MAX_ANSWERED_DURATION],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_FIRST]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_FIRST],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_LAST]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_ELAPSED_SINCE_LAST],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_IS_TEAM_FIRST]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_USER_IS_TEAM_FIRST],
    [SESSION_METRICS_COLUMNS.PRIOR_PHONE_TEAM_NOT_USER_NUM_CALLS]: v[SESSION_METRICS_COLUMNS.PRIOR_PHONE_TEAM_NOT_USER_NUM_CALLS],
    [SESSION_METRICS_COLUMNS.NEXT_C_CALL]: v[SESSION_METRICS_COLUMNS.NEXT_C_CALL],
    [SESSION_METRICS_COLUMNS.NEXT_C_CALL_BACK]: v[SESSION_METRICS_COLUMNS.NEXT_C_CALL_BACK]
  }
}

export type Snippet = {
  snippet_id: string
  is_global: boolean
  user_id: string | null
  user_group_id: string | null
  team_id: string | null
  snippet_name: string
  snippet_text: string
}

export function jsonSafeToSnippet(v: Record<string, any>): Snippet {
  return {
    snippet_id: v.snippet_id,
    is_global: v.is_global,
    user_id: v.user_id === '' ? null : v.user_id,
    user_group_id: v.user_group_id === '' ? null : v.user_group_id,
    team_id: v.team_id === '' ? null : v.team_id,
    snippet_name: v.snippet_name,
    snippet_text: v.snippet_text
  }
}

export function jsonSafeToSnippets(v: Record<string, any>): Snippet[] {
  return v.map((x: Record<string, any>) => jsonSafeToSnippet(x))
}

export type LinkedInVoiceMemoMetadata = {
  linkedin_voice_memo_id: string
  user_id: string
  team_id: string
  name: string
  file_size: number
  duration?: number
}

export const OBJECTION_COLUMNS = [
  SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT,
  SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT,
  SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT,
  SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT,
  SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT,
  SESSION_METRICS_COLUMNS.R_DENY_COUNT,
  SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT,
  SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT,
]

export const OBJECTION_COLUMNS_TO_HUMAN_READABLE: Map<SESSION_METRICS_COLUMNS, string> = new Map([
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_RELEVANT_COUNT, 'Not Relevant'],
  [SESSION_METRICS_COLUMNS.R_RESP_NOT_DECISION_MAKER_COUNT, 'Not Decision Maker'],
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_NEED_COUNT, 'No Need'],
  [SESSION_METRICS_COLUMNS.R_PRODUCT_NO_BUDGET_COUNT, 'No Budget'],
  [SESSION_METRICS_COLUMNS.R_COMPETITORS_USING_COUNT, 'Using Competitor'],
  [SESSION_METRICS_COLUMNS.R_DENY_COUNT, 'Brush off'],
  [SESSION_METRICS_COLUMNS.R_NAME_DENY_COUNT, 'Wrong Person'],
  [SESSION_METRICS_COLUMNS.R_TITLE_DENY_COUNT, 'Wrong Role'],
])

export type PersonV2 = {
  team_id: string
  platform_person_id: string
  platform: Platform
  platform_company_id: string | null
  platform_person_url: string | null
  name: string | null
  name_lower: string | null
  last_name_lower: string | null
  title: string | null
  occupation: string | null
  seniority: string | null
  location: string | null
  person_linkedin: string | null
}

export type ContactV2 = {
  team_id: string
  platform_person_id: string
  platform: Platform
  address: string
  address_type: AddressType
  phone_subtype: PROSPECT_PHONE_TYPE | null
}

export type SessionAgents = {
  session_id: string
  team_id: string
  agent_ids_str: string
  agent_types_str: string
  joined_at_str: string
  left_at_str: string
  first_user_id: string | null
  creator_agent_id: string | null
  creator_agent_type: AgentType | null
}

export function jsonSafeToSessionAgents(v: Record<string, any>): SessionAgents {
  return {
    session_id: v.session_id,
    team_id: v.team_id,
    agent_ids_str: v.agent_ids_str,
    agent_types_str: v.agent_types_str,
    joined_at_str: v.joined_at_str,
    left_at_str: v.left_at_str,
    first_user_id: v.first_user_id,
    creator_agent_id: v.creator_agent_id,
    creator_agent_type: v.creator_agent_type,
  }
}

export type SessionDetailsV2 = {
    session_id: string
    team_id: string
    started_at: Date
    cadence: string | null
    cadence_step: string | null
    prospect_list: string | null
    company_persona: string | null
    market: string | null
    lead_type: string | null
}

export function jsonSafeToSessionDetailsV2(v: Record<string, any>): SessionDetailsV2 {
  return {
    session_id: v.session_id,
    team_id: v.team_id,
    started_at: v.started_at,
    cadence: v.cadence,
    cadence_step: v.cadence_step,
    prospect_list: v.prospect_list,
    company_persona: v.company_persona,
    market: v.market,
    lead_type: v.lead_type,
  }
}

export type CallMetric = {
    session_id: string
    team_id: string
    started_at: Date

    is_trellus_telephony: boolean | null
    is_inbound: boolean | null
    is_autodial: boolean
    parallel_lines: number | null

    call_duration: number
    customer_duration: number
    live_duration: number
    customer_joined_at: number | null
    customer_live_at: number | null
    customer_live_known_at: number | null
    first_user_joined_at: number | null

    customer_dropped_duration: number | null
    agent_dropped_duration: number | null

    has_customer_voicemail: boolean
    has_agent_voicemail: boolean
    has_prerecorded_voicemail: boolean
    customer_ended: boolean | null
    sip_response_code: number | null

    customer_audio_uploaded: boolean
    customer_transcript_uploaded: boolean
}

export function jsonSafeToCallMetric(v: Record<string, any>): CallMetric {
  return {  
    session_id: v.session_id,
    team_id: v.team_id,
    started_at: v.started_at,
    is_trellus_telephony: v.is_trellus_telephony,
    is_inbound: v.is_inbound,
    is_autodial: v.is_autodial,
    parallel_lines: v.parallel_lines,
    call_duration: v.call_duration,
    customer_duration: v.customer_duration,
    live_duration: v.live_duration,
    customer_joined_at: v.customer_joined_at,
    customer_live_at: v.customer_live_at,
    customer_live_known_at: v.customer_live_known_at,
    first_user_joined_at: v.first_user_joined_at,
    customer_dropped_duration: v.customer_dropped_duration,
    agent_dropped_duration: v.agent_dropped_duration,
    has_customer_voicemail: v.has_customer_voicemail,
    has_agent_voicemail: v.has_agent_voicemail,
    has_prerecorded_voicemail: v.has_prerecorded_voicemail,
    customer_ended: v.customer_ended,
    sip_response_code: v.sip_response_code,
    customer_audio_uploaded: v.customer_audio_uploaded,
    customer_transcript_uploaded: v.customer_transcript_uploaded
  }
}

export type CounterpartData = {
  session_id: string
  starts_str: string
  ends_str: string
  values_str: string
}

export function jsonSafeToCounterpartData(v: Record<string, any>): CounterpartData {
  return {
    session_id: v.session_id,
    starts_str: v.starts_str,
    ends_str: v.ends_str,
    values_str: v.values_str
  }
}

export enum BackendTables {
    SCOPES = 'SCOPES',
    EXTERNAL_SCOPES = 'EXTERNAL_SCOPES',
    SCOPES_V2 = 'SCOPES_V2',
    TEAMS = 'TEAMS',
    USERS = 'USERS',
    USER_AUTH = 'USER_AUTH',
    PERSONS = 'PERSONS',
    SUBTEAM_VIEWS = 'SUBTEAM_VIEWS',
    SUBTEAMS = 'SUBTEAMS',
    SUBTEAM_MEMBERS = 'SUBTEAM_MEMBERS',
    USER_GROUPS = 'USER_GROUPS',
    USER_GROUP_MEMBERSHIPS = 'USER_GROUP_MEMBERSHIPS',
    USER_GROUP_FLAT_MEMBERSHIPS = 'USER_GROUP_FLAT_MEMBERSHIPS',
    EXTERNAL_ACCOUNTS = 'EXTERNAL_ACCOUNTS',
    EXTERNAL_ACCOUNT_LINKS = 'EXTERNAL_ACCOUNT_LINKS',
    EXTERNAL_ACCOUNT_MEMBERS = 'EXTERNAL_ACCOUNT_MEMBERS',
    KEYWORDS = 'KEYWORDS',
    KEYWORD_PHRASES = 'KEYWORD_PHRASES',
    KEYWORD_GROUPS = 'KEYWORD_GROUPS',
    KEYWORD_GROUP_MEMBERSHIPS = 'KEYWORD_GROUP_MEMBERSHIPS',
    TRIGGERS = 'TRIGGERS',
    TRIGGER_FORMULAS = 'TRIGGER_FORMULAS',
    CONDITIONS = 'CONDITIONS',
    PLAYS = 'PLAYS',
    CONDITION_TRACKS = 'CONDITION_TRACKS',
    CUSTOM_DEFINITIONS = 'CUSTOM_DEFINITIONS',
    SAVED_REPORTS = 'SAVED_REPORTS',
    AI_MANY_CALL_ANALYSES = 'AI_MANY_CALL_ANALYSES',
    REPORT_EMAILS = 'REPORT_EMAILS',
    REPORT_EMAIL_DATA = 'REPORT_EMAIL_DATA',
    AGENT_DATA = 'AGENT_DATA',  
    AUTODIALER_SETTINGS = 'AUTODIALER_SETTINGS',
    AUTODIALER_MAPPING = 'AUTODIALER_MAPPING',
    AUTODIALER_MAPPING_OPTIONS = 'AUTODIALER_MAPPING_OPTIONS',
    MULTIDIALER_SUBSCRIPTIONS = 'MULTIDIALER_SUBSCRIPTIONS',
    TWILIO_NUMBERS = 'TWILIO_NUMBERS',
    TWILIO_REGULATORY_BUNDLE_RULES = 'TWILIO_REGULATORY_BUNDLE_RULES',
    TWILIO_BUSINESS_PROFILES = 'TWILIO_BUSINESS_PROFILES',
    TWILIO_OUTGOING_CALLS = 'TWILIO_OUTGOING_CALLS',
    TWILIO_INCOMING_CALLS = 'TWILIO_INCOMING_CALLS',
    RECORDED_GREETINGS = 'RECORDED_GREETINGS',
    EMAIL_VALUE_PROPS = 'EMAIL_VALUE_PROPS',
    GEN_AI_PROSPECT_EMAILS = 'GEN_AI_PROSPECT_EMAILS',
    GEN_AI_LINKEDIN_MESSAGES = 'GEN_AI_LINKEDIN_MESSAGES',
    BOT_EMOTIONS = 'BOT_EMOTIONS',
    BOTS = 'BOTS',
    BOT_CALLS = 'BOT_CALLS',
    LEGAL_BOT_CALL = 'LEGAL_BOT_CALL',
    BOT_SKILLS = 'BOT_SKILLS',
    SNIPPETS = 'SNIPPETS',
    LINKEDIN_VOICE_MEMOS = 'LINKEDIN_VOICE_MEMOS',  
    SESSIONS = 'SESSIONS',
    SESSIONS_V2 = 'SESSIONS_V2',
    SESSION_STATES = 'SESSION_STATES',
    SESSION_AGENTS = 'SESSION_AGENTS',
    SESSION_COSTS = 'SESSION_COSTS',
    SESSION_ACCESS_TOKENS = 'SESSION_ACCESS_TOKENS',
    SESSION_DETAILS_V2 = 'SESSION_DETAILS_V2',
    CALL_METRICS = 'CALL_METRICS',  
    SESSION_METRICS = 'SESSION_METRICS',
    CUSTOM_METRICS = 'CUSTOM_METRICS',
    CUSTOM_DETAILS = 'CUSTOM_DETAILS',
    CALLBACK_REQUEST_DETAILS = 'CALLBACK_REQUEST_DETAILS',
    USER_PHONE_AGG = 'USER_PHONE_AGG',
    ACCOUNT_PHONE_AGG = 'ACCOUNT_PHONE_AGG',
    AUDIO = 'AUDIO',
    TRANSCRIPTS = 'TRANSCRIPTS',
    TRANSCRIPT_DATA = 'TRANSCRIPT_DATA',
    MESSAGING_DATA = 'MESSAGING_DATA',
    TRANSCRIPT_LEXEME_VECTORS = 'TRANSCRIPT_LEXEME_VECTORS',
    PERSONS_V2 = 'PERSONS_V2',
    COMPANIES = 'COMPANIES',
    CONTACTS_V2 = 'CONTACTS_V2',
    SOUND_DATA = 'SOUND_DATA',
    MODEL_LINKS = 'MODEL_LINKS',
    SOUND_RESULTS = 'SOUND_RESULTS',
    COUNTERPART_RESULTS = 'COUNTERPART_RESULTS',
    COUNTERPART_DATA = 'COUNTERPART_DATA',
    REMARK_RESULTS = 'REMARK_RESULTS',
    REMARK_DATA = 'REMARK_DATA',
    IDENTITY_RESULTS = 'IDENTITY_RESULTS',
    NOTES = 'NOTES',
    NOTE_DISPOSITIONS = 'NOTE_DISPOSITIONS',
    NOTE_REFERENCES = 'NOTE_REFERENCES',
    KEYPAD_ENTRY = 'KEYPAD_ENTRY',
    STAR_WORKFLOWS = 'STAR_WORKFLOWS',
    REVIEW_STATUSES = 'REVIEW_STATUSES',
    COMMENTS = 'COMMENTS',
    LEADERBOARD_ROOMS = 'LEADERBOARD_ROOMS',  
    LEADERBOARD_MEMBERSHIPS = 'LEADERBOARD_MEMBERSHIPS',
    SALES_FLOOR_ROOMS = 'SALES_FLOOR_ROOMS',
    SALES_FLOOR_ROOM_VISITS = 'SALES_FLOOR_ROOM_VISITS',
    SALES_FLOOR_ROOM_MEMBERSHIPS = 'SALES_FLOOR_ROOM_MEMBERSHIPS',
    TRACKER = 'TRACKER',
    TRACKER_PAYLOADS = 'TRACKER_PAYLOADS',  
    CLIENT_LOG = 'CLIENT_LOG',  
    REFINEMENT_ANNOTATIONS = 'REFINEMENT_ANNOTATIONS',
    REFINEMENT_CALL_ANNOTATIONS = 'REFINEMENT_CALL_ANNOTATIONS',
    PROSPECT_INFO = 'PROSPECT_INFO',
    CONTACTS = 'CONTACTS',
    PARTIES = 'PARTIES',
    CLIENTS = 'CLIENTS',
    DISPLAY = 'DISPLAY',
    ROLLUPS = 'ROLLUPS',
}